import { useEffect, useState, useRef, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ExternalLink,
  Star,
  Award,
  Bot,
  Send,
  Loader2,
  Globe,
  Mail,
  Phone,
  Check,
  MapPin,
  Clock,
  Play,
  Image as ImageIcon,
  Facebook,
  Instagram,
  Linkedin,
  Twitter,
  Youtube,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Crown,
  Sparkles,
  Building2,
  CheckCircle2,
  MessageSquare,
  Gift,
  Plane,
  CreditCard,
  Users,
  Tag,
  Eye,
  Volume2,
  VolumeX,
  Navigation2,
  HelpCircle,
  Shield,
  Zap,
  ListChecks,
  Share2,
  ArrowRight,
  Link2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import SEOHead from "@/components/SEOHead";
import axios from "axios";
import { useTracking } from "@/contexts/TrackingContext";
import FacebookPageEmbed from "@/components/FacebookPageEmbed";
import ShortsHeroLoop from "@/components/ShortsHeroLoop";
import ContentDetailModal from "@/components/ContentDetailModal";
import HorizontalCarousel from "@/components/HorizontalCarousel";
import ListingHorsepowerSection from "@/components/ListingHorsepowerSection";
import ShareThisListingSection from "@/components/ShareThisListingSection";
import GetYourListingStrip from "@/components/GetYourListingStrip";
import OnlyBestSections from "@/components/OnlyBestSections";
import EndorsedBy from "@/components/EndorsedBy";
import LiveEventsSection from "@/components/LiveEventsSection";
import LivecastSection from "@/components/LivecastSection";
import LanguageToggle from "@/components/LanguageToggle";
import LanguagePromptBanner from "@/components/LanguagePromptBanner";
import { useLanguage } from "@/contexts/LanguageContext";
import { buildListingShareUrl } from "@/utils/shareUrl";
import EmbedRenderer from "@/components/EmbedRenderer";
import TrustedNetworkSection from "@/components/TrustedNetworkSection";
import PodcastCard from "@/components/PodcastCard";
import BusinessInventorySection from "@/components/business/BusinessInventorySection";
import GalleryVideoPlayer from "@/components/business/GalleryVideoPlayer";
import ListingHero3D from "@/components/premium/ListingHero3D";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Social icon mapping
const socialIcons = {
  facebook: Facebook,
  instagram: Instagram,
  linkedin: Linkedin,
  twitter: Twitter,
  youtube: Youtube,
  tiktok: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
    </svg>
  ),
};

// Category-specific qualifying questions
const categoryQuestions = {
  "HVAC": ["Is this for heating or cooling?", "Is this an emergency or scheduled service?", "What type of system do you have?"],
  "Roofing": ["Is this storm damage or a routine inspection?", "What type of roofing material?", "When did you last have it inspected?"],
  "Real Estate": ["Are you looking to buy or sell?", "What's your timeline?", "What area are you interested in?"],
  "Mortgage": ["Are you refinancing or purchasing?", "Is this your first home?", "What's your credit score range?"],
  "Credit": ["Are you looking to repair or monitor credit?", "Do you know your current score?", "What's your primary goal?"],
  "Legal": ["What type of legal matter is this?", "Is this urgent?", "Have you consulted an attorney before?"],
  "Automotive": ["What type of service do you need?", "What's the make and model?", "Is this for repair or maintenance?"],
  "Home Services": ["What service do you need?", "Is this an emergency?", "When would you like the service?"],
  "default": ["What service are you interested in?", "Is this urgent or can it be scheduled?", "What's the best time to reach you?"]
};

// Day order for business hours
const dayOrder = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

// Auto-playing muted short card with a tap-to-unmute toggle.
// Plays inline on mobile + desktop. User can tap the speaker icon
// to enable sound on the one they're watching (others stay muted).
function ShortGridItem({ item, url, idx }) {
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);

  // If this short is a YouTube / Vimeo embed, render an iframe instead.
  const embedUrl = item?.embed_url;
  const provider = item?.provider;
  if (embedUrl && (provider === "youtube" || provider === "youtube_shorts" || provider === "vimeo")) {
    return (
      <div className="relative rounded-xl overflow-hidden bg-black aspect-[9/16] border border-white/10 hover:border-[#EC4899]/40 transition-all">
        <iframe
          src={embedUrl}
          title={item.title || `Short ${idx + 1}`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
          style={{ border: 0 }}
          data-testid={`short-embed-${idx}`}
        />
      </div>
    );
  }

  if (!url) return null;
  return (
    <div
      className="relative rounded-xl overflow-hidden bg-black aspect-[9/16] border border-white/10 hover:border-[#EC4899]/40 transition-all"
    >
      <video
        ref={ref}
        src={url}
        className="w-full h-full object-cover"
        autoPlay
        muted={muted}
        loop
        playsInline
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        data-testid={`short-video-${idx}`}
      />
      {/* Mute/Unmute toggle */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          const v = ref.current;
          if (!v) return;
          const next = !muted;
          setMuted(next);
          v.muted = next;
          if (!next) v.play().catch(() => {});
        }}
        className="absolute bottom-2 right-2 w-9 h-9 rounded-full bg-black/70 hover:bg-black/90 backdrop-blur flex items-center justify-center z-10"
        aria-label={muted ? "Unmute" : "Mute"}
        data-testid={`short-mute-toggle-${idx}`}
      >
        {muted ? <VolumeX className="w-4 h-4 text-white" /> : <Volume2 className="w-4 h-4 text-white" />}
      </button>
      {/* Tap-to-play overlay when paused */}
      {!playing && (
        <button
          type="button"
          onClick={() => ref.current?.play().catch(() => {})}
          className="absolute inset-0 flex items-center justify-center bg-black/40 z-0"
          aria-label="Play"
        >
          <span className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
            <Play className="w-5 h-5 text-black ml-1" />
          </span>
        </button>
      )}
    </div>
  );
}

export default function BusinessProfilePage() {
  const { slug } = useParams();
  const [business, setBusiness] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hoursExpanded, setHoursExpanded] = useState(false);
  const { trackViewContent, setBusinessContext, trackLead, trackCallClick, trackBookingClick, trackWebsiteClick, trackDirectionsClick, trackChatClick, trackOfferClick } = useTracking();

  // Bilingual support
  const { lang, t } = useLanguage();
  const [esCopy, setEsCopy] = useState(null);          // translated payload
  const [esLoading, setEsLoading] = useState(false);
  const [esError, setEsError] = useState(false);
  
  // AI Dispatch Chat State
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [sessionId] = useState(() => `biz-${slug}-${Date.now()}`);
  const [chatPhase, setChatPhase] = useState("greeting"); // greeting, qualifying, capture, complete
  const [capturedLead, setCapturedLead] = useState({ firstName: "", email: "", phone: "" });
  const [qualifyingAnswers, setQualifyingAnswers] = useState({});
  const messagesEndRef = useRef(null);
  
  // Gallery state
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  
  // Reviews & Content state
  const [reviews, setReviews] = useState([]);
  const [relatedContent, setRelatedContent] = useState([]);
  const [activeContent, setActiveContent] = useState(null);

  // Lead Capture Form (Quick Help / Claim Rewards)
  const leadFormRef = useRef(null);
  const aiChatRef = useRef(null);
  const [leadForm, setLeadForm] = useState({
    name: "", phone: "", email: "", city: "", help: "",
    contact_method: "Phone", urgency: "This week", message: "",
  });
  const [leadSubmitting, setLeadSubmitting] = useState(false);

  // Lazy-fetch Spanish copy the first time the user switches to ES.
  // ALSO: never retry on the same render cycle — guard with esError so a
  // single failure doesn't trigger an infinite re-render storm.
  useEffect(() => {
    if (lang !== "es" || !slug) return;
    if (esCopy || esLoading || esError) return;
    setEsLoading(true);
    axios
      .get(`${API}/v1/i18n/translate/${slug}?lang=es`)
      .then((r) => {
        setEsCopy(r.data || {});
      })
      .catch(() => {
        setEsError(true);
      })
      .finally(() => setEsLoading(false));
  }, [lang, slug, esCopy, esLoading, esError]);

  // Reset cached translation when the user changes language so we re-fetch
  // (without entering a retry loop on failure).
  useEffect(() => {
    if (lang === "en") {
      setEsCopy(null);
      setEsError(false);
    }
  }, [lang]);

  // Build a "localized" view of the business by overlaying Spanish copy when
  // available. Always falls back to the original English values for any field
  // the LLM didn't translate (or if translation failed).
  const localized = useMemo(() => {
    if (!business) return null;
    if (lang !== "es" || !esCopy) return business;
    const overlay = {};
    [
      "name",
      "tagline",
      "headline",
      "short_description",
      "seo_description",
      "description",
      "about_us",
      "services",
      "products",
      "specialties",
      "cta_button_text",
      "lead_form_intro",
      "lead_form_title",
    ].forEach((k) => {
      const v = esCopy[k];
      if (v !== undefined && v !== null && v !== "") overlay[k] = v;
    });
    if (Array.isArray(esCopy.faqs) && esCopy.faqs.length) overlay.faqs = esCopy.faqs;
    return { ...business, ...overlay };
  }, [business, esCopy, lang]);

  // If the visitor toggles the language BEFORE chatting, swap the greeting
  // so they immediately see (and hear) the new language. Once they've sent
  // any message, we leave the conversation as-is.
  useEffect(() => {
    if (!business) return;
    const hasInteracted = chatMessages.some((m) => m.role === "user");
    if (hasInteracted) return;
    const heroName = business.ai_agent_name || `${business.name} AI Hero`;
    const city = business.city || "";
    const cityLineEs = city ? ` en ${city}${business.state ? ", " + business.state : ""}` : "";
    const cityLineEn = city ? ` in ${city}${business.state ? ", " + business.state : ""}` : "";
    if (lang === "es") {
      setChatMessages([{
        role: "assistant",
        content: `Hola, soy ${heroName}, el AI Business Hero™ de ${business.name}${cityLineEs}.\n\nPuedo ayudarle en español: responder preguntas, reclamar ofertas, solicitar una llamada o agendar ayuda ahora.\n\n¿En qué puedo ayudarle hoy?`,
      }]);
    } else {
      setChatMessages([{
        role: "assistant",
        content: `Hi! I'm ${heroName}, the AI Business Hero™ for ${business.name}${cityLineEn}.\n\nI can help you learn more, answer questions, or connect you with our team.\n\nHow can I help you today?`,
      }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, business?.slug]);

  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [leadSource, setLeadSource] = useState("Quick Help Form");

  const scrollToLeadForm = (source = "Quick Help Form") => {
    setLeadSource(source);
    leadFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const scrollToAIChat = () => {
    aiChatRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const scrollToShorts = () => {
    document.querySelector('[data-testid="shorts-section"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submitLeadForm = async (e) => {
    e.preventDefault();
    if (!business) return;
    if (!leadForm.name.trim() || !leadForm.phone.trim()) return;
    setLeadSubmitting(true);
    try {
      const [firstName, ...rest] = leadForm.name.trim().split(" ");
      const lastName = rest.join(" ");
      const listingUrl = typeof window !== "undefined" ? window.location.href : "";
      await axios.post(`${API}/leads`, {
        business_id: business.id,
        first_name: firstName,
        last_name: lastName,
        email: leadForm.email,
        phone: leadForm.phone,
        message: `Service: ${leadForm.help}\nCity: ${leadForm.city}\nPreferred contact: ${leadForm.contact_method}\nUrgency: ${leadForm.urgency}\nMessage: ${leadForm.message}\nListing: ${listingUrl}`,
        service_interest: leadForm.help,
        lead_type: leadSource,
        source: "LocalBest.ai Listing",
        preferred_language: lang === "es" ? "Spanish" : "English",
        language_source: "visitor_selected",
        qualifying_answers: {
          city: leadForm.city,
          contact_method: leadForm.contact_method,
          urgency: leadForm.urgency,
          source_cta: leadSource,
          listing_url: listingUrl,
          preferred_language: lang === "es" ? "Spanish" : "English",
        },
      });
      try { trackLead && trackLead({ business_id: business.id, source_cta: leadSource }); } catch (_) {}
      setLeadSubmitted(true);
    } catch (err) {
      console.error("Lead submit failed", err);
    } finally {
      setLeadSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchBusiness = async () => {
      // Scroll to top when page loads
      window.scrollTo(0, 0);
      
      try {
        const res = await axios.get(`${API}/businesses/${slug}`);
        setBusiness(res.data);
        
        // Set initial greeting from AI Business Hero (category-aware + city + signature offering)
        const heroName = res.data.ai_agent_name || `${res.data.name} AI Hero`;
        const cat = (res.data.category_id || res.data.category_name || "").toLowerCase();
        const isFood = ["restaurant", "food-and-dining", "food", "dining", "bar"].some(k => cat.includes(k));
        const isRoofing = cat.includes("roofing");
        const isAutomotive = cat.includes("automotive") || cat.includes("auto");
        const isRealEstate = cat.includes("real-estate") || cat.includes("real estate");
        const isHealth = cat.includes("health") || cat.includes("wellness") || cat.includes("medical");
        const isBeauty = cat.includes("beauty") || cat.includes("salon");
        const isLegal = cat.includes("legal") || cat.includes("law");
        // Pull a signature offering: prefer first service name → first product → cleaned about_us snippet
        let signature = "";
        const svcs = res.data.services || [];
        const prods = res.data.products || [];
        if (svcs.length && (svcs[0].name || typeof svcs[0] === "string")) {
          signature = (svcs[0].name || svcs[0]).toString().trim();
        } else if (prods.length && (prods[0].name || typeof prods[0] === "string")) {
          signature = (prods[0].name || prods[0]).toString().trim();
        } else if (res.data.short_description) {
          signature = res.data.short_description.replace(/[#*_`]/g, "").split(/[.!?]/)[0].trim().slice(0, 80);
        }
        const city = (res.data.city || "").trim();
        const cityLine = city ? ` in ${city}${res.data.state ? ", " + res.data.state : ""}` : "";
        const signatureLine = signature ? ` — known for ${signature}` : "";
        let helpLine = "I can help you learn more, answer questions, or connect you with our team.";
        let promptLine = "How can I help you today?";
        if (isFood) { helpLine = "I can help you book a reservation, share today's specials, look up the menu, or arrange catering."; promptLine = "Want to book a reservation or hear today's specials?"; }
        else if (isRoofing) { helpLine = "I can help with storm damage, roof inspections, estimates, or claiming a homeowner offer."; promptLine = "Want a fast quote or to claim your homeowner reward?"; }
        else if (isAutomotive) { helpLine = "I can help you book service, get an estimate, or share hours and location."; promptLine = "Want to book service or get a quick estimate?"; }
        else if (isRealEstate) { helpLine = "I can help you schedule a showing, get a home valuation, or connect you with an agent."; promptLine = "Want to schedule a showing or chat with an agent?"; }
        else if (isHealth) { helpLine = "I can help you book an appointment, share services, or check insurance."; promptLine = "Want to book an appointment?"; }
        else if (isBeauty) { helpLine = "I can help you book an appointment, share services and pricing, or check stylist availability."; promptLine = "Want to book your next appointment?"; }
        else if (isLegal) { helpLine = "I can help you schedule a free consultation, explain our practice areas, or connect you with an attorney."; promptLine = "Want to schedule a free consultation?"; }
        // Build initial greeting in the visitor's preferred language.
        const currentLang = (typeof window !== "undefined" && localStorage.getItem("localbest:lang")) || "en";
        let greetingText;
        if (currentLang === "es") {
          greetingText = `Hola, soy ${heroName}, el AI Business Hero™ de ${res.data.name}${cityLine ? ` en ${city}${res.data.state ? ", " + res.data.state : ""}` : ""}${signature ? ` — reconocidos por ${signature}` : ""}.\n\nPuedo ayudarle en español: responder preguntas, reclamar ofertas, solicitar una llamada o agendar ayuda ahora.\n\n¿En qué puedo ayudarle hoy?`;
        } else {
          greetingText = `Hi! I'm ${heroName}, the AI Business Hero™ for ${res.data.name}${cityLine}${signatureLine}.\n\n${helpLine}\n\n${promptLine}`;
        }
        setChatMessages([
          {
            role: "assistant",
            content: greetingText,
          },
        ]);
        
        // Fetch reviews
        try {
          const reviewsRes = await axios.get(`${API}/reviews/${res.data.id}`);
          setReviews(reviewsRes.data || []);
        } catch (e) {
          console.log("No reviews yet");
        }
        
        // Fetch related content from POD
        try {
          const contentRes = await axios.get(`${API}/content/business/${res.data.id}`);
          setRelatedContent(contentRes.data || []);
        } catch (e) {
          console.log("No related content");
        }
        
      } catch (error) {
        console.error("Error fetching business:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchBusiness();
  }, [slug]);

  // Track ViewContent when business loads
  useEffect(() => {
    if (business && !isLoading) {
      // Set business context for pixel isolation
      setBusinessContext({ 
        id: business.id, 
        name: business.name,
        tracking: business.tracking 
      });
      
      // Fire ViewContent event for business profile
      trackViewContent({
        content_id: business.id || business.slug,
        content_type: 'business',
        content_name: business.name,
        business_id: business.id,
        business_name: business.name,
        category: business.category_name,
        listing_type: 'business',
        location: `${business.city}, ${business.state}`,
        is_featured: business.is_featured,
        is_best_of_best: business.is_best_of_best,
      });
    }
  }, [business, isLoading, trackViewContent, setBusinessContext]);

  const scrollToBottom = () => {
    // Use block: 'nearest' to only scroll within the chat container, not the whole page
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  useEffect(() => {
    // Only scroll after initial render
    if (chatMessages.length > 1) {
      scrollToBottom();
    }
  }, [chatMessages]);

  // Get qualifying questions based on category
  const getQualifyingQuestions = () => {
    if (!business) return categoryQuestions.default;
    const category = business.category_name || "";
    
    // Check if business has custom qualifying questions
    if (business.qualifying_questions && business.qualifying_questions.length > 0) {
      return business.qualifying_questions;
    }
    
    // Match category
    for (const [key, questions] of Object.entries(categoryQuestions)) {
      if (category.toLowerCase().includes(key.toLowerCase())) {
        return questions;
      }
    }
    return categoryQuestions.default;
  };

  const handleChatSubmit = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || isChatLoading || !business) return;

    const userMessage = chatInput.trim();
    setChatInput("");
    setChatMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setIsChatLoading(true);

    try {
      // Check if we're in lead capture phase
      if (chatPhase === "capture_name") {
        setCapturedLead(prev => ({ ...prev, firstName: userMessage }));
        setChatMessages((prev) => [
          ...prev,
          { role: "assistant", content: `Nice to meet you, ${userMessage}! What's the best email to reach you at?` },
        ]);
        setChatPhase("capture_email");
        setIsChatLoading(false);
        return;
      }
      
      if (chatPhase === "capture_email") {
        setCapturedLead(prev => ({ ...prev, email: userMessage }));
        setChatMessages((prev) => [
          ...prev,
          { role: "assistant", content: "Great! And what's the best mobile number to reach you? (Or type 'skip' to continue)" },
        ]);
        setChatPhase("capture_phone");
        setIsChatLoading(false);
        return;
      }
      
      if (chatPhase === "capture_phone") {
        const phone = userMessage.toLowerCase() === "skip" ? "" : userMessage;
        const finalLead = { ...capturedLead, phone };
        
        // Build conversation thread for POD leads dashboard
        const conversationThread = chatMessages.map(msg => ({
          role: msg.role,
          content: msg.content,
          timestamp: new Date().toISOString()
        }));
        
        // Submit lead with full conversation thread
        try {
          await axios.post(`${API}/leads`, {
            business_id: business.id,
            first_name: finalLead.firstName,
            email: finalLead.email,
            phone: finalLead.phone || null,
            qualifying_answers: { ...qualifyingAnswers, preferred_language: lang === "es" ? "Spanish" : "English" },
            lead_type: "AI Dispatch Chat",
            preferred_language: lang === "es" ? "Spanish" : "English",
            language_source: "visitor_selected",
            message: JSON.stringify(conversationThread), // Full chat thread for POD dashboard
          });
        } catch (err) {
          console.error("Lead capture error:", err);
        }
        
        setChatMessages((prev) => [
          ...prev,
          { 
            role: "assistant", 
            content: `Perfect, thank you ${finalLead.firstName}!\n\nWe'll have a representative from ${business.name} contact you shortly.\n\n🎁 Since you used LocalBest.ai, you also qualify for:\n\n🏖️ A FREE Resort Stay (Orlando, Vegas, or Cancun)\n💳 A $500 Travel Savings Card\n\nCourtesy of One Force Live, sponsor of this directory!\n\nIs there anything else I can help you with?` 
          },
        ]);
        setChatPhase("complete");
        setIsChatLoading(false);
        return;
      }

      // Check if user wants to be dispatched/contacted or get coupon
      const dispatchKeywords = ["dispatch", "contact", "call me", "schedule", "book", "appointment", "talk to someone", "speak with", "get started", "sign up", "interested", "coupon", "discount"];
      const wantsDispatch = dispatchKeywords.some(kw => userMessage.toLowerCase().includes(kw));
      
      if (wantsDispatch && chatPhase !== "complete") {
        setChatMessages((prev) => [
          ...prev,
          { role: "assistant", content: "I'd be happy to connect you with our team and get you that discount! Let me get a few details.\n\nWhat's your first name?" },
        ]);
        setChatPhase("capture_name");
        setIsChatLoading(false);
        return;
      }

      // Normal AI chat response
      const response = await axios.post(`${API}/chat`, {
        message: userMessage,
        business_id: business.id,
        session_id: sessionId,
        preferred_language: lang,
      });

      let botResponse = response.data.response;
      
      // After a few exchanges, prompt for dispatch
      if (chatMessages.length >= 4 && chatPhase === "greeting") {
        botResponse += "\n\nWould you like me to connect you with our team? I can pass your details along right away!";
      }

      setChatMessages((prev) => [
        ...prev,
        { role: "assistant", content: botResponse },
      ]);
    } catch (error) {
      console.error("Chat error:", error);
      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "I apologize, but I'm having trouble connecting. Would you like me to have our team contact you directly?",
        },
      ]);
    } finally {
      setIsChatLoading(false);
    }
  };

  // Category-aware quick action buttons for chat
  const quickActions = useMemo(() => {
    const cat = (business?.category_id || business?.category_name || "").toLowerCase();
    const map = {
      restaurant: [
        { label: "Book a Reservation", message: "I'd like to book a reservation" },
        { label: "Order Catering", message: "Can you help me with catering?" },
        { label: "Today's Specials", message: "What are today's specials?" },
        { label: "Hours & Menu", message: "What are your hours and what's on the menu?" },
      ],
      "food-and-dining": [
        { label: "Book a Reservation", message: "I'd like to book a reservation" },
        { label: "Order Catering", message: "Can you help me with catering?" },
        { label: "Today's Specials", message: "What are today's specials?" },
        { label: "Hours & Menu", message: "What are your hours and what's on the menu?" },
      ],
      roofing: [
        { label: "Do I Need a Roof Inspection?", message: "Do I need a roof inspection?" },
        { label: "Check Storm Damage", message: "I think I have storm damage" },
        { label: "Claim Homeowner Offer", message: "How do I claim my homeowner offer?" },
        { label: "Request a Call Back", message: "Please have someone call me back" },
      ],
      construction: [
        { label: "Request a Quote", message: "Can I get a quote for a project?" },
        { label: "Project Timeline", message: "How long do projects typically take?" },
        { label: "Service Areas", message: "What areas do you serve?" },
        { label: "Request a Call Back", message: "Please have someone call me back" },
      ],
      "home-services": [
        { label: "Request a Quote", message: "Can I get a quote?" },
        { label: "Service Areas", message: "What areas do you serve?" },
        { label: "Emergency Help", message: "I need urgent help" },
        { label: "Request a Call Back", message: "Please have someone call me back" },
      ],
      automotive: [
        { label: "Book Service", message: "I'd like to book a service appointment" },
        { label: "Get an Estimate", message: "Can I get an estimate?" },
        { label: "Hours & Location", message: "What are your hours and location?" },
        { label: "Request a Call Back", message: "Please have someone call me back" },
      ],
      "real-estate": [
        { label: "Schedule a Showing", message: "I'd like to schedule a showing" },
        { label: "Get a Home Valuation", message: "Can I get a home valuation?" },
        { label: "Available Listings", message: "What listings do you have available?" },
        { label: "Talk to an Agent", message: "I'd like to speak with an agent" },
      ],
      legal: [
        { label: "Free Consultation", message: "Can I get a free consultation?" },
        { label: "Practice Areas", message: "What types of cases do you handle?" },
        { label: "Hours & Location", message: "What are your hours?" },
        { label: "Speak with an Attorney", message: "I'd like to speak with an attorney" },
      ],
      "health-and-wellness": [
        { label: "Book an Appointment", message: "I'd like to book an appointment" },
        { label: "Services Offered", message: "What services do you offer?" },
        { label: "Insurance Accepted", message: "Do you accept my insurance?" },
        { label: "Hours & Location", message: "What are your hours and location?" },
      ],
      beauty: [
        { label: "Book an Appointment", message: "I'd like to book an appointment" },
        { label: "Services & Prices", message: "What are your services and prices?" },
        { label: "Stylist Availability", message: "Who is available this week?" },
        { label: "Hours & Location", message: "What are your hours and location?" },
      ],
      fitness: [
        { label: "Try a Free Class", message: "Can I try a free class?" },
        { label: "Membership Options", message: "What are your membership options?" },
        { label: "Class Schedule", message: "What does the class schedule look like?" },
        { label: "Hours & Location", message: "What are your hours and location?" },
      ],
      financial: [
        { label: "Free Consultation", message: "Can I get a free consultation?" },
        { label: "Services Offered", message: "What services do you offer?" },
        { label: "Get Started", message: "How do I get started?" },
        { label: "Speak with an Advisor", message: "I'd like to speak with an advisor" },
      ],
    };
    // Match by category_id first, then partial keyword in category_name
    if (map[cat]) return map[cat];
    for (const k of Object.keys(map)) {
      if (cat.includes(k)) return map[k];
    }
    // Default — generic but conversion-focused (no more "Dispatch Team")
    return [
      { label: "Request a Quote", message: "Can I get a quote?" },
      { label: "Hours & Services", message: "What are your hours and services?" },
      { label: "Claim My Offer", message: "How do I claim my LocalBest offer?" },
      { label: "Request a Call Back", message: "Please have someone call me back" },
    ];
  }, [business?.category_id, business?.category_name]);

  const handleQuickAction = (message) => {
    setChatInput(message);
  };

  const galleryItems = (business?.gallery || []).map(g => typeof g === 'string' ? g : g?.url).filter(Boolean);
  const allImages = [
    business?.logo_url,
    ...(business?.images || []),
    ...galleryItems,
    ...(business?.promo_images || []),
  ].filter(Boolean);

  if (isLoading) {
    return (
      <div className="page-container min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#7C3AED] animate-spin" />
      </div>
    );
  }

  if (!business) {
    return (
      <div className="page-container min-h-screen flex flex-col items-center justify-center">
        <h1 className="text-2xl text-white font-bold mb-4">Business not found</h1>
        <Link to="/directory">
          <Button variant="outline" className="btn-secondary">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Directory
          </Button>
        </Link>
      </div>
    );
  }

  // Build location string for display
  const location = [business.city, business.state].filter(Boolean).join(", ");
  const fullAddress = [
    business.street_address,
    business.address_line_2,
    business.city,
    business.state,
    business.zip_code
  ].filter(Boolean).join(", ") || business.address || location;

  // SEO-friendly URL components
  const citySlug = (business.city || "local").toLowerCase().replace(/\s+/g, "-");
  const stateSlug = (business.state || "").toLowerCase();
  const cityState = stateSlug ? `${citySlug}-${stateSlug}` : citySlug;

  // Map URL - use coordinates if available, otherwise city
  const mapQuery = business.latitude && business.longitude 
    ? `${business.latitude},${business.longitude}`
    : encodeURIComponent(fullAddress || location || business.name);
  const mapEmbedUrl = `https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${mapQuery}&zoom=14`;
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${mapQuery}`;

  // Subtle brand accent — applied to primary CTA only. Falls back to LocalBest purple.
  const brandPrimary = business.brand_primary_color || null;
  const brandStyle = brandPrimary
    ? { background: brandPrimary, color: '#fff', borderColor: brandPrimary }
    : {};
  const brandBadgeStyle = brandPrimary
    ? { background: `${brandPrimary}20`, borderColor: `${brandPrimary}50`, color: brandPrimary }
    : {};

  // Cinematic Commercial TV block (re-used on mobile inline + desktop standalone)
  const renderCinematicTV = () => {
    const videoUrl = business.video_url || business.promo_video;
    if (!videoUrl) return null;
    return (
      <div className="relative w-full overflow-hidden" data-testid="cinematic-commercial-video-wrapper">
        {/* Scaled wrapper: on mobile, image is enlarged to 171% so the TV's inner screen fills the viewport width */}
        <div className="relative md:w-full md:mx-0 w-[171%] mx-[-35.5%]">
          <img
            src="https://customer-assets.emergentagent.com/job_b515077c-a195-4ba1-b1ea-7a33c6e19b28/artifacts/dtloe834_ChatGPT%20Image%20Jun%205%2C%202026%2C%2005_03_10%20PM.png"
            alt={`${business.name} cinematic commercial`}
            className="block w-full h-auto select-none pointer-events-none"
            draggable="false"
          />
          <div
            className="absolute overflow-hidden bg-black"
            style={{ top: "15%", left: "21%", width: "58.5%", height: "57.5%" }}
            data-testid="cinematic-commercial-video"
          >
            {(() => {
              if (videoUrl.includes("youtube") || videoUrl.includes("youtu.be")) {
                const videoId = videoUrl.includes("youtu.be/")
                  ? videoUrl.split("youtu.be/")[1]?.split("?")[0]
                  : videoUrl.split("v=")[1]?.split("&")[0];
                return (
                  <iframe
                    title={`${business.name} commercial`}
                    src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&rel=0&modestbranding=1&playsinline=1`}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                );
              } else if (videoUrl.includes("vimeo")) {
                const vimeoId = videoUrl.split("vimeo.com/")[1]?.split("?")[0];
                return (
                  <iframe
                    title={`${business.name} commercial`}
                    src={`https://player.vimeo.com/video/${vimeoId}?autoplay=1&muted=1&loop=1`}
                    className="w-full h-full"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                  />
                );
              }
              return (
                <video
                  src={videoUrl}
                  controls
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-full object-cover"
                  onLoadedData={(e) => {
                    e.target.play().catch(() => {});
                  }}
                />
              );
            })()}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="page-container min-h-screen" data-testid="business-profile" style={brandPrimary ? { ['--brand-primary']: brandPrimary } : {}}>
      {/* SEO Head with meta tags and structured data */}
      <SEOHead business={business} />
      
      {/* POD v2 Tracking - Google Consent Mode v2 (if consent enabled) */}
      {(business.tracking?.cookie_consent_enabled || business.tracking?.consent_mode) && (
        <script
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              // Default consent state - denied until user consents
              gtag('consent', 'default', {
                'ad_storage': 'denied',
                'ad_user_data': 'denied',
                'ad_personalization': 'denied',
                'analytics_storage': 'denied'
              });
            `
          }}
        />
      )}
      
      {/* POD v2 Tracking - GTM Container (if configured) */}
      {business.tracking?.gtm_container_id && (
        <>
          <script
            dangerouslySetInnerHTML={{
              __html: `
                (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer','${business.tracking.gtm_container_id}');
              `
            }}
          />
          <noscript>
            <iframe 
              src={`https://www.googletagmanager.com/ns.html?id=${business.tracking.gtm_container_id}`}
              height="0" 
              width="0" 
              style={{display: 'none', visibility: 'hidden'}}
            />
          </noscript>
        </>
      )}
      
      {/* POD v2 Tracking - Meta Pixel with ViewContent event */}
      {(business.tracking?.meta_pixel_id || business.tracking_pixels?.meta_pixel_id) && (
        <script
          dangerouslySetInnerHTML={{
            __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '${business.tracking?.meta_pixel_id || business.tracking_pixels?.meta_pixel_id}');
              fbq('track', 'PageView');
              fbq('track', 'ViewContent', {
                content_name: '${business.name.replace(/'/g, "\\'")}',
                content_category: '${business.category_name || "business"}',
                content_type: 'business',
                content_ids: ['${business.id}']
              });
            `
          }}
        />
      )}
      
      {/* POD v2 Tracking - GA4 with view_item event */}
      {(business.tracking?.ga4_measurement_id || business.tracking_pixels?.ga4_measurement_id) && (
        <>
          <script async src={`https://www.googletagmanager.com/gtag/js?id=${business.tracking?.ga4_measurement_id || business.tracking_pixels?.ga4_measurement_id}`} />
          <script
            dangerouslySetInnerHTML={{
              __html: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', '${business.tracking?.ga4_measurement_id || business.tracking_pixels?.ga4_measurement_id}');
                gtag('event', 'view_item', {
                  item_id: '${business.id}',
                  item_name: '${business.name.replace(/'/g, "\\'")}',
                  item_category: '${business.category_name || "business"}'
                });
              `
            }}
          />
        </>
      )}
      
      {/* POD v2 Tracking - Google Ads Conversion */}
      {(business.tracking?.google_ads_conversion_id || business.tracking_pixels?.google_ads_id) && (
        <>
          <script async src={`https://www.googletagmanager.com/gtag/js?id=${business.tracking?.google_ads_conversion_id || business.tracking_pixels?.google_ads_id}`} />
          <script
            dangerouslySetInnerHTML={{
              __html: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', '${business.tracking?.google_ads_conversion_id || business.tracking_pixels?.google_ads_id}');
              `
            }}
          />
        </>
      )}
      
      {/* TikTok Pixel */}
      {business.tracking_pixels?.tiktok_pixel_id && (
        <script
          dangerouslySetInnerHTML={{
            __html: `
              !function (w, d, t) {
                w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
                ttq.load('${business.tracking_pixels.tiktok_pixel_id}');
                ttq.page();
              }(window, document, 'ttq');
            `
          }}
        />
      )}
      
      {/* LinkedIn Insight Tag */}
      {business.tracking_pixels?.linkedin_partner_id && (
        <script
          dangerouslySetInnerHTML={{
            __html: `
              _linkedin_partner_id = "${business.tracking_pixels.linkedin_partner_id}";
              window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
              window._linkedin_data_partner_ids.push(_linkedin_partner_id);
              (function(l) {
              if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
              window.lintrk.q=[]}
              var s = document.getElementsByTagName("script")[0];
              var b = document.createElement("script");
              b.type = "text/javascript";b.async = true;
              b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
              s.parentNode.insertBefore(b, s);})(window.lintrk);
            `
          }}
        />
      )}
      
      {/* Twitter/X Pixel */}
      {business.tracking_pixels?.twitter_pixel_id && (
        <script
          dangerouslySetInnerHTML={{
            __html: `
              !function(e,t,n,s,u,a){e.twq||(s=e.twq=function(){s.exe?s.exe.apply(s,arguments):s.queue.push(arguments);
              },s.version='1.1',s.queue=[],u=t.createElement(n),u.async=!0,u.src='https://static.ads-twitter.com/uwt.js',
              a=t.getElementsByTagName(n)[0],a.parentNode.insertBefore(u,a))}(window,document,'script');
              twq('config','${business.tracking_pixels.twitter_pixel_id}');
            `
          }}
        />
      )}
      
      {/* Custom Script (for any additional pixels like TV/Radio) */}
      {business.tracking_pixels?.custom_script && (
        <script
          dangerouslySetInnerHTML={{
            __html: business.tracking_pixels.custom_script
          }}
        />
      )}
      
      {/* Cover / Background Image */}
      {business.background_image_url && (
        <div className="relative w-full h-48 md:h-64 overflow-hidden" data-testid="cover-image">
          <img src={business.background_image_url} alt={`${business.name} cover`}
            className="w-full h-full object-cover" />
          {/* Cinematic vertical fade — image fades into #020817 page background so hero text stays readable */}
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(to bottom, rgba(2,8,23,0) 35%, rgba(2,8,23,0.55) 58%, rgba(2,8,23,0.88) 75%, #020817 100%)",
            }}
          />
          {/* Desktop-only horizontal readability wash — darkens left/center-left behind long titles */}
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none hidden md:block"
            style={{
              background:
                "linear-gradient(to right, rgba(2,8,23,0.55) 0%, rgba(2,8,23,0.30) 45%, rgba(2,8,23,0.05) 75%, rgba(2,8,23,0) 100%)",
            }}
          />
        </div>
      )}

      {/* Hero Section */}
      <ListingHero3D business={business}>
      <section className={`py-8 border-b border-white/5 ${business.background_image_url ? '-mt-20 relative z-10' : ''}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Bilingual hint (only fires for Spanish-leaning browsers; dismissible) */}
          <div className="mb-4"><LanguagePromptBanner /></div>

          {/* Back to Directory removed per design — visitors use the LocalBest.ai logo (top-left of nav) to return */}

          <div className="flex flex-col md:flex-row md:items-start gap-6">
            {/* Logo or Initials */}
            {business.logo_url && !business.use_initials ? (
              <motion.img
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                src={business.logo_url}
                alt={`${business.name} logo - ${business.category_name} in ${location}`}
                className="w-24 h-24 rounded-2xl object-cover bg-white/5 cursor-pointer"
                onClick={() => { setGalleryIndex(0); setGalleryOpen(true); }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling?.classList.remove('hidden');
                }}
              />
            ) : null}
            {/* Initials Fallback */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className={`w-24 h-24 rounded-2xl bg-gradient-to-br from-[#7C3AED] to-[#EC4899] flex items-center justify-center cursor-pointer ${business.logo_url && !business.use_initials ? 'hidden' : ''}`}
            >
              <span className="text-3xl font-bold text-white">
                {business.initials || business.name?.split(' ').map(w => w[0]).join('').substring(0, 3).toUpperCase() || '?'}
              </span>
            </motion.div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-start gap-3 mb-2">
                {/* H1: Business Name + Primary Service + City (SEO) — full-width on mobile so the name doesn't wrap aggressively */}
                <h1 className="text-[28px] leading-[1.1] sm:text-3xl md:text-4xl font-bold font-['Outfit'] text-white w-full md:flex-1 md:min-w-0 break-words">
                  {business.name}
                  {location && <span className="block md:inline text-[#94A3B8] text-base sm:text-lg md:text-xl font-normal md:ml-2 mt-1 md:mt-0">– {business.category_name} in {location}</span>}
                </h1>
                <div className="flex items-center gap-2 md:gap-3 w-full md:w-auto md:ml-auto">
                  <LanguageToggle size="sm" />
                  {/* Compact share button — opens native share sheet on mobile, copy link otherwise */}
                  <button
                    type="button"
                    onClick={async () => {
                      const shareUrl = buildListingShareUrl(business);
                      const shareText = `Check out ${business.name} on LocalBest.ai`;
                      try {
                        if (navigator.share) {
                          await navigator.share({ title: business.name, text: shareText, url: shareUrl });
                          axios.post(`${API}/v1/horsepower/${business.slug}/share`, null, { params: { network: "native_top" } }).catch(() => {});
                        } else {
                          await navigator.clipboard.writeText(shareUrl);
                          toast.success("Link copied");
                          axios.post(`${API}/v1/horsepower/${business.slug}/share`, null, { params: { network: "copy_top" } }).catch(() => {});
                        }
                      } catch (_) { /* cancelled */ }
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm font-medium transition"
                    aria-label="Share this listing"
                    data-testid="hero-share-btn"
                  >
                    <Share2 className="w-4 h-4" /> Share
                  </button>
                </div>
              </div>
              
              {/* Badges */}
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {business.listing_kind === "creator" && (
                  <span className="px-2 py-1 rounded-full bg-gradient-to-r from-[#FBBF24]/20 to-[#EC4899]/20 border border-[#FBBF24]/30 text-[#FBBF24] text-xs flex items-center gap-1" data-testid="creator-badge">
                    <Crown className="w-3 h-3" /> Best of Best Creator
                  </span>
                )}
                {(business.is_best_of_best || business.is_ai_certified) && (
                  <span className="best-badge">
                    <Award className="w-3 h-3" /> Best of Best
                  </span>
                )}
                {business.is_verified && (
                  <span className="px-2 py-1 rounded-full bg-[#10B981]/20 border border-[#10B981]/30 text-[#10B981] text-xs flex items-center gap-1">
                    <Shield className="w-3 h-3" /> Verified Business
                  </span>
                )}
                <span
                  className="font-medium"
                  style={brandPrimary ? { color: brandPrimary } : { color: '#7C3AED' }}
                >{business.category_name}</span>
              </div>
              
              <div className="flex items-center gap-1 text-[#F59E0B] mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-4 h-4 ${i < Math.round(business.rating) ? "fill-current" : "opacity-30"}`}
                  />
                ))}
                <Eye className="w-4 h-4 text-[#94A3B8] ml-2" />
              </div>
              
              {/* SEO Description - use seo_description if available */}
              {(() => {
                const src = localized || business;
                // When the visitor is on Spanish, prefer the translated
                // short_description over the (often English) seo_description.
                const heroDesc = lang === "es"
                  ? (src.short_description || src.seo_description || src.description || "")
                  : (src.seo_description || src.short_description || src.description || "");
                const tagline = src.short_description && src.short_description !== heroDesc ? src.short_description : "";
                return (
                  <>
                    <p className="text-[#94A3B8] max-w-2xl">
                      {heroDesc}
                    </p>

                    {/* Funnel Subheadline — guides the visitor to action */}
                    <p className="text-white/90 mt-3 max-w-2xl text-sm md:text-base" data-testid="hero-funnel-subheadline">
                      Need help from a trusted local {business.category_name?.toLowerCase() || "expert"}{location ? ` in ${location}` : ""}? Ask our AI assistant, claim your LocalBest rewards, or request a fast call from {business.name}.
                    </p>

                    {/* Tagline — only render if it's genuinely different from the hero description above */}
                    {tagline && (
                      <p className="text-[#00E5FF] text-sm font-medium mt-2 italic">
                        {tagline}
                      </p>
                    )}
                  </>
                );
              })()}
              
              {/* Social Links — MOVED to bottom of page (below Promote This Business) to keep visitors on the page */}
            </div>

            {/* MOBILE — Cinematic Commercial appears between hero description and CTA buttons.
                Uses negative-margin viewport trick to break out of the hero's max-w-7xl + px-* container for true full-bleed. */}
            {(business.video_url || business.promo_video) && (
              <div
                className="md:hidden relative w-screen left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] -mt-2"
                data-testid="cinematic-commercial-mobile"
              >
                {renderCinematicTV()}
              </div>
            )}

            {/* Contact Actions - Funnel CTA Stack */}
            <div className="flex flex-col gap-3 w-full md:w-64 flex-shrink-0" data-testid="hero-cta-stack">
              {/* PRIMARY 1 — Claim Rewards (uses brand color if available) */}
              <Button
                onClick={() => scrollToLeadForm("Claim Rewards (Hero)")}
                className={brandPrimary ? "w-full text-white font-semibold shadow-lg hover:opacity-90" : "w-full bg-gradient-to-r from-[#F59E0B] to-[#EC4899] hover:from-[#EC4899] hover:to-[#F59E0B] text-white font-semibold shadow-lg"}
                style={brandPrimary ? brandStyle : {}}
                data-testid="hero-claim-rewards-btn"
              >
                <Gift className="w-4 h-4 mr-2" /> Claim My Rewards
              </Button>
              {/* PRIMARY 2 — Ask AI Assistant */}
              <Button
                onClick={scrollToAIChat}
                className="w-full bg-gradient-to-r from-[#7C3AED] to-[#00E5FF] hover:from-[#00E5FF] hover:to-[#7C3AED] text-white font-semibold shadow-lg"
                data-testid="hero-ask-ai-btn"
              >
                <Bot className="w-4 h-4 mr-2" /> Ask AI Assistant
              </Button>
              {/* PRIMARY 3 — Call Now */}
              {business.contact_phone && (
                <a href={`tel:${business.contact_phone.replace(/\D/g, '')}`} className="block" onClick={() => trackCallClick(business)} data-testid="hero-call-now-btn">
                  <Button className="w-full bg-[#10B981] hover:bg-[#059669] text-white font-semibold">
                    <Phone className="w-4 h-4 mr-2" /> Call Now
                  </Button>
                </a>
              )}
              {/* SECONDARY — Custom CTA (e.g. owner's external link), demoted */}
              {business.cta_text && (
                business.cta_url ? (
                  <a href={business.cta_url} target="_blank" rel="noopener noreferrer" className="block" data-testid="cta-button">
                    <Button variant="outline" className="w-full bg-transparent border-white/15 text-[#94A3B8] hover:text-white hover:bg-white/5 text-sm">
                      {business.cta_text}
                    </Button>
                  </a>
                ) : business.cta_phone ? (
                  <a href={`tel:${business.cta_phone.replace(/\D/g, '')}`} className="block" data-testid="cta-button">
                    <Button variant="outline" className="w-full bg-transparent border-white/15 text-[#94A3B8] hover:text-white hover:bg-white/5 text-sm">
                      <Phone className="w-4 h-4 mr-2" /> {business.cta_text}
                    </Button>
                  </a>
                ) : null
              )}
              {/* SECONDARY — Website link, demoted */}
              {business.website && (
                <a
                  href={business.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-center text-xs text-[#64748B] hover:text-[#00E5FF] transition-colors"
                  onClick={() => trackWebsiteClick(business, business.website)}
                  data-testid="hero-website-link"
                >
                  Visit official website →
                </a>
              )}
            </div>
          </div>
        </div>
      </section>
      </ListingHero3D>

      {/* Cinematic Commercial — DESKTOP standalone full-bleed below hero (mobile has its own inline render inside the hero). */}
      {(business.video_url || business.promo_video) && (
        <section
          className="hidden md:block relative w-full overflow-hidden bg-transparent md:-mt-6 lg:-mt-8"
          data-testid="cinematic-commercial-section"
        >
          {renderCinematicTV()}
        </section>
      )}

      {/* === Best of Best Press Release — header image + teaser + "Read Full Article" modal === */}
      {business.is_best_of_best && (business.press_release_image || business.press_release_body) && (
        <BestOfBestPressRelease business={business} />
      )}

      {/* Main Content Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-5 gap-8">
          
          {/* Left Column - AI Dispatch Chat (Primary) */}
          {/* min-w-0 lets the grid track shrink below its content's min-content size on mobile */}
          <div className="lg:col-span-3 space-y-6 min-w-0">

            {/* MOBILE — Shorts Hero Loop pinned near the top (Instagram/Facebook reel style) */}
            {(business.shorts || []).length > 0 && (
              <div className="lg:hidden flex justify-center" data-testid="mobile-shorts-loop">
                <ShortsHeroLoop
                  shorts={business.shorts}
                  brandColor="#F59E0B"
                  size="mobile"
                />
              </div>
            )}
            
            {/* AI DISPATCH CHAT BOT - PRIMARY CAPTURE */}
            <motion.div
              ref={aiChatRef}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card rounded-2xl overflow-hidden border-2 border-[#00E5FF]/30 scroll-mt-24"
              style={{ boxShadow: "0 0 40px rgba(0, 229, 255, 0.1)" }}
            >
              {/* Chat Header */}
              <div className="p-4 bg-gradient-to-r from-[#7C3AED]/20 to-[#00E5FF]/20 border-b border-white/10">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <img
                      src={business.ai_agent_avatar || `https://api.dicebear.com/7.x/bottts/svg?seed=${business.slug || business.name || "hero"}`}
                      alt={business.ai_agent_name || "AI Business Hero"}
                      onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = `https://api.dicebear.com/7.x/bottts/svg?seed=${business.slug || "hero"}`; }}
                      className="w-14 h-14 rounded-full border-2 border-[#00E5FF] bg-[#0F172A] object-cover"
                    />
                    <div className="absolute bottom-0 right-0 w-4 h-4 bg-[#10B981] rounded-full border-2 border-[#020817]"></div>
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg">{business.ai_agent_name || `${business.name} AI Hero`}</p>
                    <p className="text-[#00E5FF] text-sm flex items-center gap-1">
                      <Bot className="w-4 h-4" /> AI Business Hero™ • Online Now
                    </p>
                  </div>
                </div>
              </div>

              {/* Chat Messages - Larger window */}
              <div className="h-[450px] overflow-y-auto p-4 space-y-4 bg-[#0a0f1a]">
                {chatMessages.map((msg, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    {msg.role === "assistant" && (
                      <img
                        src={business.ai_agent_avatar || `https://api.dicebear.com/7.x/bottts/svg?seed=${business.slug || "hero"}`}
                        alt=""
                        onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = `https://api.dicebear.com/7.x/bottts/svg?seed=${business.slug || "hero"}`; }}
                        className="w-8 h-8 rounded-full mr-2 flex-shrink-0 bg-[#0F172A] object-cover"
                      />
                    )}
                    <div
                      className={`max-w-[80%] p-4 rounded-2xl text-sm whitespace-pre-wrap ${
                        msg.role === "user"
                          ? "bg-[#00E5FF] text-[#0F172A] rounded-br-md font-medium"
                          : "bg-white/10 text-[#E2E8F0] rounded-bl-md"
                      }`}
                    >
                      {msg.content}
                    </div>
                  </motion.div>
                ))}
                {isChatLoading && (
                  <div className="flex justify-start">
                    <img
                      src={business.ai_agent_avatar || `https://api.dicebear.com/7.x/bottts/svg?seed=${business.slug || "hero"}`}
                      alt=""
                      onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = `https://api.dicebear.com/7.x/bottts/svg?seed=${business.slug || "hero"}`; }}
                      className="w-8 h-8 rounded-full mr-2 bg-[#0F172A] object-cover"
                    />
                    <div className="bg-white/10 p-4 rounded-2xl rounded-bl-md">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-[#00E5FF] rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></span>
                        <span className="w-2 h-2 bg-[#00E5FF] rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></span>
                        <span className="w-2 h-2 bg-[#00E5FF] rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Quick Action Buttons */}
              {chatPhase === "greeting" && chatMessages.length < 3 && (
                <div className="px-4 py-3 border-t border-white/5 bg-white/5 flex flex-wrap gap-2">
                  {quickActions.map((action, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleQuickAction(action.message)}
                      className="px-4 py-2 rounded-full bg-[#00E5FF]/10 text-[#00E5FF] text-sm hover:bg-[#00E5FF]/20 transition-colors border border-[#00E5FF]/30"
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Chat Input */}
              <form onSubmit={handleChatSubmit} className="p-4 border-t border-white/10 bg-[#0a0f1a]">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <Input
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder={
                      chatPhase === "capture_name" ? "Enter your first name..." :
                      chatPhase === "capture_email" ? "Enter your email..." :
                      chatPhase === "capture_phone" ? "Enter your phone or type 'skip'..." :
                      "Type your message..."
                    }
                    className="flex-1 min-w-0 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-full h-12 px-4 sm:px-5"
                    disabled={isChatLoading}
                    data-testid="ai-dispatch-chat-input"
                  />
                  <Button
                    type="submit"
                    size="icon"
                    disabled={isChatLoading || !chatInput.trim()}
                    className="bg-[#00E5FF] hover:bg-[#00E5FF]/80 text-[#0F172A] rounded-full w-12 h-12"
                    data-testid="ai-dispatch-chat-send"
                  >
                    <Send size={20} />
                  </Button>
                </div>
              </form>
            </motion.div>

            {/* FUNNEL OFFER STACK */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="glass-card p-6 rounded-2xl bg-gradient-to-br from-[#7C3AED]/10 via-transparent to-[#F59E0B]/10 border border-[#F59E0B]/30"
            >
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold font-['Outfit'] text-white mb-2">
                  Claim Your LocalBest Rewards
                </h2>
                <p className="text-[#94A3B8]">
                  Connect with {business.name} through LocalBest.ai and unlock a discount coupon plus exclusive travel rewards.
                </p>
              </div>
              
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="p-4 rounded-xl bg-white/5 text-center">
                  <Tag className="w-10 h-10 text-[#F59E0B] mx-auto mb-3" />
                  <h3 className="text-white font-semibold mb-1">{business.category_name || "Business"} Coupon</h3>
                  {business.coupon_code ? (
                    <p className="text-[#00E5FF] font-mono text-lg">{business.coupon_code}</p>
                  ) : (
                    <p className="text-[#94A3B8] text-sm">Exclusive discount</p>
                  )}
                </div>
                <div className="p-4 rounded-xl bg-white/5 text-center">
                  <Plane className="w-10 h-10 text-[#10B981] mx-auto mb-3" />
                  <h3 className="text-white font-semibold mb-1">FREE Resort Stay</h3>
                  <p className="text-[#94A3B8] text-sm">Orlando, Vegas, or Cancun</p>
                </div>
                <div className="p-4 rounded-xl bg-white/5 text-center">
                  <CreditCard className="w-10 h-10 text-[#00E5FF] mx-auto mb-3" />
                  <h3 className="text-white font-semibold mb-1">$500 Travel Card</h3>
                  <p className="text-[#94A3B8] text-sm">Savings on future trips</p>
                </div>
              </div>

              <div className="text-center">
                <Button
                  onClick={() => scrollToLeadForm("Claim Rewards (Section)")}
                  className="bg-gradient-to-r from-[#F59E0B] to-[#EC4899] hover:from-[#EC4899] hover:to-[#F59E0B] text-white font-semibold px-8"
                  data-testid="claim-rewards-cta-btn"
                >
                  <Gift className="w-4 h-4 mr-2" /> Claim My Rewards
                </Button>
                <p className="text-center text-[#64748B] text-xs mt-3">
                  Rewards delivered via LocalBest.ai after verification.
                </p>
              </div>
            </motion.div>

            {/* LEAD CAPTURE FORM — Request Help / Claim Rewards */}
            <motion.div
              ref={leadFormRef}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-6 rounded-2xl border border-[#7C3AED]/30 scroll-mt-24"
              data-testid="lead-capture-form-section"
            >
              <div className="mb-5">
                <h2 className="text-2xl font-bold font-['Outfit'] text-white mb-2 flex items-center gap-2">
                  <Send className="w-5 h-5 text-[#7C3AED]" /> Request Help from {business.name}
                </h2>
                <p className="text-[#94A3B8] text-sm">
                  Fill out the quick form below — {business.name} or the LocalBest.ai team will follow up shortly.
                </p>
              </div>
              {leadSubmitted ? (
                <div className="p-6 rounded-xl bg-[#10B981]/10 border border-[#10B981]/30 text-center" data-testid="lead-form-success">
                  <CheckCircle2 className="w-12 h-12 text-[#10B981] mx-auto mb-3" />
                  <h3 className="text-white font-semibold text-lg mb-1">Thank you — your request has been received.</h3>
                  <p className="text-[#94A3B8] text-sm">
                    {business.name} or the LocalBest.ai team may contact you shortly via your preferred method.
                  </p>
                </div>
              ) : (
                <form onSubmit={submitLeadForm} className="grid md:grid-cols-2 gap-3" data-testid="lead-form">
                  <Input
                    required
                    placeholder="Full Name *"
                    value={leadForm.name}
                    onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                    className="bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                    data-testid="lead-form-name"
                  />
                  <Input
                    required
                    type="tel"
                    placeholder="Phone Number *"
                    value={leadForm.phone}
                    onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                    className="bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                    data-testid="lead-form-phone"
                  />
                  <Input
                    type="email"
                    placeholder="Email Address"
                    value={leadForm.email}
                    onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                    className="bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                    data-testid="lead-form-email"
                  />
                  <Input
                    placeholder="City"
                    value={leadForm.city}
                    onChange={(e) => setLeadForm({ ...leadForm, city: e.target.value })}
                    className="bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                    data-testid="lead-form-city"
                  />
                  <Input
                    placeholder="What do you need help with?"
                    value={leadForm.help}
                    onChange={(e) => setLeadForm({ ...leadForm, help: e.target.value })}
                    className="bg-white/5 border-white/10 text-white placeholder:text-[#64748B] md:col-span-2"
                    data-testid="lead-form-help"
                  />
                  <select
                    value={leadForm.contact_method}
                    onChange={(e) => setLeadForm({ ...leadForm, contact_method: e.target.value })}
                    className="bg-white/5 border border-white/10 text-white rounded-md h-10 px-3 text-sm"
                    data-testid="lead-form-contact-method"
                  >
                    <option className="bg-[#0F172A]">Phone</option>
                    <option className="bg-[#0F172A]">Text</option>
                    <option className="bg-[#0F172A]">Email</option>
                  </select>
                  <select
                    value={leadForm.urgency}
                    onChange={(e) => setLeadForm({ ...leadForm, urgency: e.target.value })}
                    className="bg-white/5 border border-white/10 text-white rounded-md h-10 px-3 text-sm"
                    data-testid="lead-form-urgency"
                  >
                    <option className="bg-[#0F172A]">ASAP</option>
                    <option className="bg-[#0F172A]">This week</option>
                    <option className="bg-[#0F172A]">This month</option>
                    <option className="bg-[#0F172A]">Just researching</option>
                  </select>
                  <textarea
                    rows={3}
                    placeholder="Optional message"
                    value={leadForm.message}
                    onChange={(e) => setLeadForm({ ...leadForm, message: e.target.value })}
                    className="bg-white/5 border border-white/10 text-white placeholder:text-[#64748B] rounded-md p-3 text-sm md:col-span-2"
                    data-testid="lead-form-message"
                  />
                  <p className="text-xs text-[#64748B] md:col-span-2">
                    Source: <span className="text-[#94A3B8]">{leadSource}</span>
                  </p>
                  <Button
                    type="submit"
                    disabled={leadSubmitting}
                    className="md:col-span-2 bg-gradient-to-r from-[#7C3AED] to-[#EC4899] hover:from-[#EC4899] hover:to-[#7C3AED] text-white font-semibold h-11"
                    data-testid="lead-form-submit-btn"
                  >
                    {leadSubmitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>) : (<>Send Request</>)}
                  </Button>
                </form>
              )}
            </motion.div>

            {/* Offers & Promotions from Dashboard */}
            {business.offers && business.offers.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card p-6 rounded-2xl"
                data-testid="offers-section"
              >
                <h2 className="text-xl font-bold font-['Outfit'] text-white mb-4 flex items-center gap-2">
                  <Gift className="w-5 h-5 text-[#F59E0B]" /> Special Offers
                </h2>
                <div className="space-y-4">
                  {business.offers.map((offer, idx) => (
                    <div key={offer.id || idx} className="p-4 bg-white/5 rounded-xl border border-white/10 hover:border-[#F59E0B]/30 transition-colors">
                      <div className="flex flex-col md:flex-row gap-4">
                        {offer.image_url && (
                          <img src={offer.image_url} alt={offer.title || `Offer ${idx + 1}`}
                            className="w-full md:w-40 h-32 object-cover rounded-lg flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          {offer.title && (
                            <h3 className="text-white font-semibold text-lg mb-1">{offer.title}</h3>
                          )}
                          {offer.description && (
                            <p className="text-[#94A3B8] text-sm mb-3">{offer.description}</p>
                          )}
                          {offer.link && (
                            <a href={offer.link} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-[#00E5FF] text-sm hover:underline">
                              Learn More <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                      </div>
                      {offer.video_url && (
                        <div className="mt-3 aspect-video rounded-lg overflow-hidden bg-black/50">
                          <video src={offer.video_url} controls playsInline className="w-full h-full" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Video Section — moved to full-width "Cinematic Commercial" above the main grid */}
            {false && (business.video_url || business.promo_video) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card p-6 rounded-2xl"
              >
                <h2 className="text-xl font-bold font-['Outfit'] text-white mb-4 flex items-center gap-2">
                  <Play className="w-5 h-5 text-[#7C3AED]" /> See {business.name} in Action
                </h2>
                <p className="text-[#94A3B8] text-sm mb-4">
                  Watch how {business.name} helps local {business.city ? `${business.city} ` : ""}customers.
                </p>
                <div className="aspect-video rounded-xl overflow-hidden bg-black/50">
                  {(() => {
                    const videoUrl = business.video_url || business.promo_video;
                    if (videoUrl.includes("youtube") || videoUrl.includes("youtu.be")) {
                      // YouTube - add autoplay and loop params
                      const videoId = videoUrl.includes("youtu.be/") 
                        ? videoUrl.split("youtu.be/")[1]?.split("?")[0]
                        : videoUrl.split("v=")[1]?.split("&")[0];
                      return (
                        <iframe
                          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&rel=0`}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      );
                    } else if (videoUrl.includes("vimeo")) {
                      const vimeoId = videoUrl.split("vimeo.com/")[1]?.split("?")[0];
                      return (
                        <iframe
                          src={`https://player.vimeo.com/video/${vimeoId}?autoplay=1&muted=1&loop=1`}
                          className="w-full h-full"
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                        />
                      );
                    }
                    // Direct video file - autoplay, loop, with controls for sound
                    return (
                      <video 
                        src={videoUrl} 
                        controls 
                        autoPlay 
                        loop 
                        muted
                        playsInline
                        className="w-full h-full"
                        onLoadedData={(e) => {
                          // Start playing and let user unmute
                          e.target.play().catch(() => {});
                        }}
                      />
                    );
                  })()}
                </div>
                <p className="text-xs text-[#64748B] mt-2 text-center">Click the video for sound controls</p>
                <div className="mt-4 text-center">
                  <Button
                    onClick={() => scrollToLeadForm("Post-Video CTA")}
                    className="bg-gradient-to-r from-[#7C3AED] to-[#EC4899] hover:from-[#EC4899] hover:to-[#7C3AED] text-white font-semibold"
                    data-testid="post-video-cta-btn"
                  >
                    Request Help After Watching
                  </Button>
                </div>
              </motion.div>
            )}

            {/* BEST FOR — quick fit check */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-6 rounded-2xl"
              data-testid="best-for-section"
            >
              <h2 className="text-xl font-bold font-['Outfit'] text-white mb-3 flex items-center gap-2">
                <ListChecks className="w-5 h-5 text-[#10B981]" /> Best For Customers Who Need:
              </h2>
              <ul className="space-y-2 mb-5">
                {((business.best_for && business.best_for.length > 0) ? business.best_for : [
                  `Trusted ${business.category_name?.toLowerCase() || "service"} help${location ? ` in ${location}` : ""}`,
                  `A fast quote or estimate from ${business.name}`,
                  `Local expertise${business.service_areas?.length ? ` across ${business.service_areas.slice(0, 3).join(", ")}` : ""}`,
                  `Help understanding the next step for your project`,
                  `Connecting through LocalBest.ai for rewards and incentives`,
                ]).map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-[#E2E8F0] text-sm">
                    <Check className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => scrollToLeadForm("Best For CTA")}
                variant="outline"
                className="border-[#10B981]/40 text-[#10B981] hover:bg-[#10B981]/10"
                data-testid="best-for-cta-btn"
              >
                Find Out If They Can Help Me
              </Button>
            </motion.div>

            {/* WHY LOCALBEST FEATURES THEM — trust block */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-6 rounded-2xl border border-[#7C3AED]/20"
              data-testid="why-featured-section"
            >
              <h2 className="text-xl font-bold font-['Outfit'] text-white mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#7C3AED]" /> Why LocalBest.ai Features {business.name}
              </h2>
              <p className="text-[#94A3B8] text-sm leading-relaxed mb-2">
                LocalBest.ai highlights selected local businesses based on category fit, local service area, reputation signals, responsiveness, customer experience, and ability to serve customers in their market.
              </p>
              <p className="text-[#E2E8F0] text-sm leading-relaxed">
                {business.name} is featured for customers looking for {business.category_name?.toLowerCase() || "professional service"} support{location ? ` in ${location} and the surrounding area` : ""}.
              </p>
            </motion.div>

            {/* About Us (Rich Text) */}
            {(localized?.about_us || business.about_us) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card p-6 rounded-2xl"
              >
                <h2 className="text-2xl font-bold font-['Outfit'] text-white mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-[#7C3AED]" /> {t("sections.about")} {business.name}
                </h2>
                <div 
                  className="text-[#E2E8F0] prose prose-invert prose-sm max-w-none prose-headings:text-white prose-headings:font-['Outfit'] prose-h2:text-xl prose-h3:text-lg prose-p:leading-relaxed prose-p:mb-3 prose-ul:my-3 prose-li:mb-1"
                  dangerouslySetInnerHTML={{ __html: (localized?.about_us || business.about_us) }}
                />
                {/* Final CTA stack at bottom of About */}
                <div className="mt-6 pt-5 border-t border-white/10">
                  <p className="text-white font-semibold mb-3 text-sm">Ready to get help?</p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={() => scrollToLeadForm("About Us — Claim Rewards")}
                      className="bg-gradient-to-r from-[#F59E0B] to-[#EC4899] hover:from-[#EC4899] hover:to-[#F59E0B] text-white text-sm"
                      data-testid="about-claim-rewards-btn"
                    >
                      <Gift className="w-4 h-4 mr-1.5" /> Claim Rewards
                    </Button>
                    <Button
                      onClick={scrollToAIChat}
                      className="bg-gradient-to-r from-[#7C3AED] to-[#00E5FF] hover:from-[#00E5FF] hover:to-[#7C3AED] text-white text-sm"
                      data-testid="about-ask-ai-btn"
                    >
                      <Bot className="w-4 h-4 mr-1.5" /> Ask AI
                    </Button>
                    {business.contact_phone && (
                      <a href={`tel:${business.contact_phone.replace(/\D/g, '')}`} onClick={() => trackCallClick(business)}>
                        <Button className="bg-[#10B981] hover:bg-[#059669] text-white text-sm" data-testid="about-call-btn">
                          <Phone className="w-4 h-4 mr-1.5" /> Call Now
                        </Button>
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Photo & Media Gallery (combines images[] and gallery[]) */}
            {(() => {
              const photos = [...(business.images || [])];
              const galleryItems = (business.gallery || []).filter(g => typeof g !== 'string' || g);
              const galleryPhotos = galleryItems.filter(g => {
                if (typeof g === 'string') return true;
                const t = g.type || 'image';
                return t === 'image' && !g.provider;
              }).map(g => typeof g === 'string' ? g : g.url);
              // Anything playable: direct mp4 video, YouTube/Vimeo iframe, audio
              const galleryMedia = galleryItems.filter(g => {
                if (typeof g === 'string') return false;
                if (g.provider === 'youtube' || g.provider === 'vimeo') return !!g.embed_url;
                if (g.provider === 'audio') return !!g.url;
                return g.type === 'video' && !!g.url;
              });
              const allPhotos = [...new Set([...photos, ...galleryPhotos])].filter(Boolean);
              const shorts = (business.shorts || []).map(s => typeof s === 'string' ? s : s.url).filter(Boolean);

              if (allPhotos.length === 0 && galleryMedia.length === 0 && shorts.length === 0) return null;
              return (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glass-card p-6 rounded-2xl"
                  data-testid="media-gallery"
                >
                  <h2 className="text-xl font-bold font-['Outfit'] text-white mb-4 flex items-center gap-2">
                    <ImageIcon className="w-5 h-5 text-[#7C3AED]" /> Gallery
                  </h2>
                  {allPhotos.length > 0 && (
                    <HorizontalCarousel
                      testid="gallery-photos-carousel"
                      cardWidth={{ mobile: 45, tablet: 30, desktop: 23 }}
                      className="mb-4"
                    >
                      {allPhotos.slice(0, 16).map((img, idx) => (
                        <img
                          key={idx}
                          src={img}
                          alt={`${business.name} photo ${idx + 1}`}
                          className="aspect-square w-full rounded-lg object-cover cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => { setGalleryIndex(photos.indexOf(img) !== -1 ? photos.indexOf(img) + 1 : idx + 1); setGalleryOpen(true); }}
                        />
                      ))}
                    </HorizontalCarousel>
                  )}
                  {galleryMedia.length > 0 && (
                    <HorizontalCarousel
                      testid="gallery-videos-carousel"
                      cardWidth={{ mobile: 80, tablet: 50, desktop: 40 }}
                    >
                      {galleryMedia.map((item, idx) => {
                        if (item.provider === 'youtube' || item.provider === 'vimeo') {
                          return (
                            <div key={idx} className="aspect-video w-full rounded-lg overflow-hidden bg-black">
                              <iframe
                                src={item.embed_url}
                                title={item.title || item.caption || `Video ${idx + 1}`}
                                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                loading="lazy"
                                className="w-full h-full"
                                style={{ border: 0 }}
                              />
                            </div>
                          );
                        }
                        if (item.provider === 'audio') {
                          return (
                            <div key={idx} className="aspect-video w-full rounded-lg bg-gradient-to-br from-[#7C3AED]/30 to-[#EC4899]/20 flex flex-col items-center justify-center p-4">
                              <span className="text-sm text-white font-medium mb-3 truncate w-full text-center">{item.title || item.caption || 'Audio'}</span>
                              <audio src={item.url} controls className="w-full" />
                            </div>
                          );
                        }
                        // Direct mp4/webm file — uses GalleryVideoPlayer to
                        // avoid the black-box-on-mobile bug (no poster + no
                        // preload). Muted autoplay loop + tap-for-sound is the
                        // universally-working UX.
                        return (
                          <GalleryVideoPlayer
                            key={idx}
                            src={item.url}
                            poster={item.thumbnail_url || item.poster_url}
                            caption={item.title || item.caption}
                            testid={`gallery-video-${idx}`}
                          />
                        );
                      })}
                    </HorizontalCarousel>
                  )}
                </motion.div>
              );
            })()}

            {/* Our LocalBest Trusted Network™ */}
            <TrustedNetworkSection slug={business.slug} />

            {/* SHORTS / REELS — vertical mobile videos */}
            {(business.shorts || []).length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card p-6 rounded-2xl"
                data-testid="shorts-section"
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold font-['Outfit'] text-white flex items-center gap-2">
                    <Play className="w-5 h-5 text-[#EC4899]" /> Shorts & Reels
                  </h2>
                  <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-gradient-to-r from-[#EC4899] to-[#7C3AED] text-white">
                    Vertical
                  </span>
                </div>
                <HorizontalCarousel
                  testid="shorts-carousel"
                  cardWidth={{ mobile: 45, tablet: 28, desktop: 22 }}
                >
                  {(business.shorts || []).map((s, idx) => {
                    const item = typeof s === 'string' ? { url: s } : (s || {});
                    const url = item.url;
                    const isEmbed = item.provider && item.embed_url && (item.provider === "youtube" || item.provider === "youtube_shorts" || item.provider === "vimeo");
                    if (!url && !isEmbed) return null;
                    return (
                      <ShortGridItem key={item.id || idx} item={item} url={url} idx={idx} />
                    );
                  })}
                </HorizontalCarousel>
                <p className="text-xs text-[#64748B] mt-3 text-center">Tap the speaker to play with sound</p>
              </motion.div>
            )}

            {/* Facebook embed moved to right column on desktop — rendered there for better placement */}

            {/* YouTube Embed — DISABLED on public listing per user request (still editable in dashboard) */}
            {false && business.youtube_embed && (() => {
              const match = business.youtube_embed.match(/(?:watch\?v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]+)/);
              const videoId = match?.[1];
              if (!videoId) return null;
              return (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glass-card p-6 rounded-2xl"
                  data-testid="youtube-embed"
                >
                  <h2 className="text-xl font-bold font-['Outfit'] text-white mb-4 flex items-center gap-2">
                    <Youtube className="w-5 h-5 text-[#FF0000]" /> YouTube
                  </h2>
                  <div className="aspect-video rounded-xl overflow-hidden bg-black/50">
                    <iframe
                      title="YouTube Video"
                      src={`https://www.youtube.com/embed/${videoId}?rel=0`}
                      width="100%"
                      height="100%"
                      style={{ border: "none" }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </motion.div>
              );
            })()}

            {/* Custom HTML Embeds — uses EmbedRenderer so widget <script> tags actually execute */}
            {business.custom_embeds && business.custom_embeds.length > 0 && (
              <div className="space-y-4" data-testid="custom-embeds">
                {business.custom_embeds.map((embed, idx) => (
                  embed.code && (
                    <motion.div
                      key={embed.id || idx}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="glass-card p-6 rounded-2xl"
                    >
                      {embed.title && (
                        <h2 className="text-xl font-bold font-['Outfit'] text-white mb-4">
                          {embed.title}
                        </h2>
                      )}
                      <EmbedRenderer html={embed.code} className="overflow-hidden rounded-lg" />
                    </motion.div>
                  )
                ))}
              </div>
            )}

            {/* Widget Embed Section */}
            {business.widget_embed_code && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card p-6 rounded-2xl"
              >
                <h2 className="text-xl font-bold font-['Outfit'] text-white mb-4">
                  Reviews & More
                </h2>
                <EmbedRenderer html={business.widget_embed_code} />
              </motion.div>
            )}

            {/* INVENTORY — properties / vehicles / RVs / furniture uploaded by this listing */}
            <BusinessInventorySection business={business} />

            {/* LATEST UPDATES — blog posts, news, videos published from the business dashboard */}
            {relatedContent.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card p-6 rounded-2xl"
                data-testid="latest-updates-section"
              >
                <h2 className="text-xl font-bold font-['Outfit'] text-white mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#F59E0B]" /> Latest Updates from {business.name}
                </h2>
                <HorizontalCarousel
                  testid="latest-updates-carousel"
                  cardWidth={{ mobile: 70, tablet: 45, desktop: 30 }}
                >
                  {[...relatedContent].sort((a, b) => {
                    const ta = (b.published_at || b.created_at || "").localeCompare(a.published_at || a.created_at || "");
                    return ta;
                  }).map((item, idx) => {
                    const ytMatch = (item.video_url || item.content_url || "").match(/(?:watch\?v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]+)/);
                    const videoId = ytMatch?.[1];
                    const img = item.thumbnail_url || item.featured_image || item.image_url;
                    const description = item.description || item.excerpt || item.body || (item.content || "").toString();
                    const cleanDesc = description.replace(/<[^>]*>/g, "").slice(0, 200);
                    return (
                    <button
                      type="button"
                      key={idx}
                      onClick={() => setActiveContent(item)}
                      className="text-left p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors group flex flex-col focus:outline-none focus:ring-2 focus:ring-[#00E5FF]/40 h-full w-full"
                      data-testid={`content-card-${idx}`}
                    >
                      {videoId ? (
                        <div className="aspect-video rounded-lg overflow-hidden mb-3 bg-black/50 relative">
                          <img
                            src={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`}
                            alt={item.title}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/20 transition">
                            <div className="w-12 h-12 rounded-full bg-white/95 flex items-center justify-center">
                              <Play className="w-5 h-5 text-black ml-0.5" fill="black" />
                            </div>
                          </div>
                        </div>
                      ) : item.video_url ? (
                        <div className="aspect-video rounded-lg overflow-hidden mb-3 bg-black relative">
                          <video
                            src={item.video_url}
                            muted
                            playsInline
                            preload="metadata"
                            className="w-full h-full object-cover pointer-events-none"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/20 transition">
                            <div className="w-12 h-12 rounded-full bg-white/95 flex items-center justify-center">
                              <Play className="w-5 h-5 text-black ml-0.5" fill="black" />
                            </div>
                          </div>
                        </div>
                      ) : img ? (
                        <img
                          src={img}
                          alt={item.title}
                          className="w-full aspect-video object-cover rounded-lg mb-3 group-hover:opacity-90 transition-opacity"
                        />
                      ) : (
                        <div className="aspect-video rounded-lg mb-3 bg-gradient-to-br from-[#7C3AED]/30 to-[#00E5FF]/20 flex items-center justify-center">
                          <Sparkles className="w-10 h-10 text-white/40" />
                        </div>
                      )}
                      <Badge className="mb-2 bg-[#7C3AED]/20 text-[#7C3AED] text-xs capitalize self-start">
                        {(item.content_type || "update").replace(/_/g, " ")}
                      </Badge>
                      <h3 className="text-white font-medium mb-1 line-clamp-2">{item.title}</h3>
                      {cleanDesc && (
                        <p className="text-[#94A3B8] text-sm line-clamp-3 flex-1">{cleanDesc}</p>
                      )}
                      <span className="mt-3 text-[#00E5FF] text-sm font-medium group-hover:underline">
                        {item.video_url || videoId ? "Watch video →" : "Read more →"}
                      </span>
                    </button>
                  );})}
                </HorizontalCarousel>
              </motion.div>
            )}
          </div>

          {/* Right Column - Business Info & Affiliate */}
          <div className="lg:col-span-2 space-y-6 min-w-0">

            {/* SIDEBAR — Shorts Hero Loop (vertical preview, top priority) — desktop only */}
            {(business.shorts || []).length > 0 && (
              <div className="hidden lg:block" data-testid="sidebar-shorts-loop">
                <ShortsHeroLoop
                  shorts={business.shorts}
                  brandColor={brandPrimary}
                />
              </div>
            )}

            {/* SIDEBAR — Call / Request Help (priority #1) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-6 rounded-2xl border border-[#10B981]/30 bg-gradient-to-br from-[#10B981]/10 to-transparent"
              data-testid="sidebar-help-card"
            >
              <h3 className="text-white font-bold text-lg mb-3 flex items-center gap-2">
                <Zap className="w-5 h-5 text-[#10B981]" /> Need help fast?
              </h3>
              <p className="text-[#94A3B8] text-sm mb-4">
                Call or request a fast call back from {business.name}.
              </p>
              <div className="space-y-2">
                {business.contact_phone && (
                  <a href={`tel:${business.contact_phone.replace(/\D/g, '')}`} className="block" onClick={() => trackCallClick(business)}>
                    <Button className="w-full bg-[#10B981] hover:bg-[#059669] text-white font-semibold" data-testid="sidebar-call-btn">
                      <Phone className="w-4 h-4 mr-2" /> Call Now
                    </Button>
                  </a>
                )}
                <Button
                  onClick={() => scrollToLeadForm("Sidebar — Request Help")}
                  variant="outline"
                  className="w-full border-[#10B981]/40 text-[#10B981] hover:bg-[#10B981]/10"
                  data-testid="sidebar-request-help-btn"
                >
                  Request a Call Back
                </Button>
              </div>
            </motion.div>

            {/* SIDEBAR — Claim Rewards (priority #2) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-6 rounded-2xl border border-[#F59E0B]/30 bg-gradient-to-br from-[#F59E0B]/10 to-transparent"
              data-testid="sidebar-rewards-card"
            >
              <h3 className="text-white font-bold text-lg mb-2 flex items-center gap-2">
                <Gift className="w-5 h-5 text-[#F59E0B]" /> LocalBest Rewards
              </h3>
              <p className="text-[#94A3B8] text-sm mb-4">
                Coupon, free resort stay & $500 travel card — available through LocalBest.ai.
              </p>
              <Button
                onClick={() => scrollToLeadForm("Sidebar — Claim Rewards")}
                className="w-full bg-gradient-to-r from-[#F59E0B] to-[#EC4899] hover:from-[#EC4899] hover:to-[#F59E0B] text-white font-semibold"
                data-testid="sidebar-claim-rewards-btn"
              >
                Claim My Rewards
              </Button>
            </motion.div>

            {/* Location & Contact Info - H2 Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-6 rounded-2xl"
            >
              <h2 className="text-xl font-bold font-['Outfit'] text-white mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#7C3AED]" /> Location & Contact Info
              </h2>
              <div className="space-y-4">
                {/* Website - Click to visit */}
                {business.website && (
                  <a
                    href={business.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-[#94A3B8] hover:text-[#00E5FF] transition-colors group"
                    onClick={() => trackWebsiteClick(business, business.website)}
                    data-testid="website-link"
                  >
                    <Globe className="w-5 h-5 text-[#7C3AED] group-hover:text-[#00E5FF]" />
                    <span className="truncate">{business.website.replace(/^https?:\/\//, "")}</span>
                    <ExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                )}
                {/* Email - Click to email */}
                {business.contact_email && (
                  <a
                    href={`mailto:${business.contact_email}`}
                    className="flex items-center gap-3 text-[#94A3B8] hover:text-[#00E5FF] transition-colors"
                  >
                    <Mail className="w-5 h-5 text-[#7C3AED]" />
                    {business.contact_email}
                  </a>
                )}
                {/* Phone - Click to call */}
                {business.contact_phone && (
                  <a
                    href={`tel:${business.contact_phone.replace(/\D/g, '')}`}
                    className="flex items-center gap-3 text-[#94A3B8] hover:text-[#10B981] transition-colors font-medium"
                  >
                    <Phone className="w-5 h-5 text-[#10B981]" />
                    {business.contact_phone}
                  </a>
                )}
                {/* Full Address - NAP consistent */}
                {fullAddress && (
                  <div className="flex items-start gap-3 text-[#94A3B8]">
                    <MapPin className="w-5 h-5 text-[#7C3AED] mt-0.5 flex-shrink-0" />
                    <div>
                      {business.street_address && <p>{business.street_address}</p>}
                      {business.address_line_2 && <p>{business.address_line_2}</p>}
                      <p>{[business.city, business.state, business.zip_code].filter(Boolean).join(", ")}</p>
                      {business.is_service_area_business && business.service_areas?.length > 0 && (
                        <p className="text-xs text-[#7C3AED] mt-1">
                          Service Area: {business.service_areas.join(", ")}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {/* Business Hours Summary */}
                {business.hours_notes && (
                  <div className="flex items-center gap-3 text-[#94A3B8]">
                    <Clock className="w-5 h-5 text-[#7C3AED]" />
                    <span>{business.hours_notes}</span>
                  </div>
                )}
              </div>
            </motion.div>
            
            {/* Map & Directions - H2 Section */}
            {location && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card p-6 rounded-2xl"
              >
                <h2 className="text-xl font-bold font-['Outfit'] text-white mb-4 flex items-center gap-2">
                  <Navigation2 className="w-5 h-5 text-[#7C3AED]" /> Map & Directions
                </h2>
                {/* Embedded Map */}
                <div className="rounded-xl overflow-hidden mb-4 bg-white/5 aspect-video">
                  <iframe
                    title={`Map of ${business.name} in ${location}`}
                    src={mapEmbedUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
                {/* Get Directions Button */}
                <a
                  href={directionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#7C3AED]/20 text-[#7C3AED] hover:bg-[#7C3AED]/30 transition-colors"
                  onClick={() => trackDirectionsClick(business)}
                  data-testid="get-directions-btn"
                >
                  <Navigation2 className="w-4 h-4" /> Get Directions
                </a>
              </motion.div>
            )}

            {/* Services - H2 Section */}
            {(localized?.services || business.services) && (localized?.services || business.services).length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card p-6 rounded-2xl"
              >
                <h2 className="text-xl font-bold font-['Outfit'] text-white mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#7C3AED]" /> {t("sections.services")}
                </h2>
                <div className="space-y-2">
                  {(localized?.services || business.services).map((service, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors"
                    >
                      <Check className="w-4 h-4 text-[#10B981]" />
                      <span className="text-[#E2E8F0] text-sm">{service}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
            
            {/* Business Hours — collapsible to save space; safe-wraps long values */}
            {business.hours && Object.keys(business.hours).length > 0 && (() => {
              const todayKey = new Date().toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
              const todayRaw = business.hours[todayKey];
              const fmtHours = (h) => {
                if (typeof h === "string") return { text: h, closed: h.toLowerCase() === "closed" };
                if (h && typeof h === "object") {
                  if (h.closed) return { text: "Closed", closed: true };
                  return { text: `${h.open || "9:00 AM"} – ${h.close || "5:00 PM"}`, closed: false };
                }
                return { text: "—", closed: false };
              };
              const today = fmtHours(todayRaw);
              return (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glass-card rounded-2xl overflow-hidden"
                  data-testid="business-hours-card"
                >
                  <button
                    type="button"
                    onClick={() => setHoursExpanded((v) => !v)}
                    className="w-full flex items-center justify-between gap-3 px-5 sm:px-6 py-4 text-left"
                    aria-expanded={hoursExpanded}
                    data-testid="business-hours-toggle"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Clock className="w-5 h-5 text-[#7C3AED] flex-shrink-0" />
                      <span className="text-base sm:text-lg font-bold font-['Outfit'] text-white">Business Hours</span>
                    </div>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`text-xs sm:text-sm text-right truncate max-w-[160px] sm:max-w-none ${today.closed ? "text-[#EF4444]" : "text-[#10B981]"}`}>
                        {today.closed ? "Closed today" : `Open today · ${today.text}`}
                      </span>
                      <ChevronDown
                        className={`w-4 h-4 text-[#94A3B8] flex-shrink-0 transition-transform ${hoursExpanded ? "rotate-180" : ""}`}
                      />
                    </div>
                  </button>
                  <AnimatePresence initial={false}>
                    {hoursExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="px-5 sm:px-6 pb-5 space-y-1.5">
                          {dayOrder.map((day) => {
                            const h = business.hours[day];
                            if (!h) return null;
                            const { text, closed } = fmtHours(h);
                            const isToday = todayKey === day;
                            return (
                              <div
                                key={day}
                                className={`flex flex-wrap justify-between items-center gap-x-3 gap-y-1 py-1.5 px-3 rounded-lg ${isToday ? "bg-[#7C3AED]/10 border border-[#7C3AED]/30" : ""}`}
                                data-testid={`hours-row-${day}`}
                              >
                                <span className={`capitalize text-sm ${isToday ? "text-[#7C3AED] font-medium" : "text-[#94A3B8]"}`}>
                                  {day}
                                </span>
                                <span className={`text-sm break-words text-right ${closed ? "text-[#EF4444]" : "text-[#E2E8F0]"}`}>
                                  {text}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })()}

            {/* Reviews */}
            {reviews.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card p-6 rounded-2xl"
              >
                <h2 className="text-xl font-bold font-['Outfit'] text-white mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5 text-[#F59E0B]" /> Reviews
                </h2>
                <div className="space-y-4">
                  {reviews.slice(0, 3).map((review, idx) => (
                    <div key={idx} className="p-4 bg-white/5 rounded-xl">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-[#7C3AED]/20 flex items-center justify-center text-white font-bold">
                          {review.author_name?.charAt(0) || "U"}
                        </div>
                        <div>
                          <p className="text-white font-medium">{review.author_name}</p>
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-3 h-3 text-[#F59E0B] ${i < review.rating ? "fill-current" : "opacity-30"}`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                      <p className="text-[#94A3B8] text-sm line-clamp-3">{review.content}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
            
            {/* === LISTING HORSEPOWER & COMMUNITY MOMENTUM === */}
            <ListingHorsepowerSection slug={business.slug} businessName={business.name} />

            {/* FAQ Section - H2 for SEO */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-6 rounded-2xl"
            >
              <h2 className="text-xl font-bold font-['Outfit'] text-white mb-4 flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-[#7C3AED]" /> Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {/* Default FAQ items for SEO - will be replaced by business.faq if available */}
                {(business.faq && business.faq.length > 0 ? business.faq : [
                  {
                    question: `What services does ${business.name} offer?`,
                    answer: business.services?.length > 0 
                      ? `${business.name} offers: ${business.services.join(", ")}.`
                      : `${business.name} provides quality ${business.category_name?.toLowerCase() || "professional"} services. Contact them directly for a full list of services.`
                  },
                  {
                    question: `Where is ${business.name} located?`,
                    answer: location 
                      ? `${business.name} is located in ${location}. ${business.is_service_area_business ? `They also serve: ${business.service_areas?.join(", ") || "surrounding areas"}.` : ""}`
                      : `Contact ${business.name} directly for location information.`
                  },
                  {
                    question: `How can I contact ${business.name}?`,
                    answer: `You can reach ${business.name} ${business.contact_phone ? `by phone at ${business.contact_phone}` : ""}${business.contact_phone && business.contact_email ? " or " : ""}${business.contact_email ? `by email at ${business.contact_email}` : ""}${business.website ? `. Visit their website at ${business.website}` : ""}.`
                  },
                  {
                    question: `Does ${business.name} have an AI Business Hero?`,
                    answer: `Yes! ${business.name} has their own AI Business Hero that can answer questions, provide information about services, and help you connect with the business 24/7.`
                  },
                  {
                    question: `Why is ${business.name} listed in the Best of Best Directory?`,
                    answer: `${business.name} is featured in the LocalBest.ai Best of Best Directory because they meet our quality standards for ${business.category_name || "professional services"}${location ? ` in ${location}` : ""}. Being listed here provides them with strong local SEO signals, a high-quality backlink, and visibility to customers searching for trusted ${business.category_name?.toLowerCase() || "service"} providers.`
                  },
                  {
                    question: `How does this listing help ${business.name} get found online?`,
                    answer: `This directory listing is structured for Google, Bing, and AI search engines like ChatGPT. It includes consistent NAP (Name, Address, Phone) data, structured schema markup, and SEO-optimized content that helps ${business.name} appear in local searches like "best ${business.category_name?.toLowerCase() || "services"} near me" or "${business.category_name?.toLowerCase() || "services"} in ${business.city || "your area"}".`
                  }
                ]).map((faqItem, idx) => (
                  <div key={idx} className="p-4 bg-white/5 rounded-xl">
                    <h3 className="text-white font-medium mb-2">{faqItem.question}</h3>
                    <p className="text-[#94A3B8] text-sm">{faqItem.answer}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Facebook embed — DISABLED on public listing per user request (still editable in dashboard) */}
            {false && business.facebook_embed && (
              <FacebookPageEmbed pageUrl={business.facebook_embed} businessName={business.name} compact />
            )}

            {/* AFFILIATE NETWORK BUTTON — moved to bottom (lower priority than lead conversion) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-6 rounded-2xl bg-gradient-to-br from-[#F59E0B]/10 to-transparent border border-[#F59E0B]/30"
            >
              <div className="text-center">
                <Users className="w-12 h-12 text-[#F59E0B] mx-auto mb-4" />
                <h2 className="text-xl font-bold font-['Outfit'] text-white mb-2">
                  Promote This Business
                </h2>
                <p className="text-[#94A3B8] text-sm mb-4">
                  Earn commissions by referring customers to {business.name}
                </p>
                {business.affiliate_signup_url || business.affiliate_link ? (
                  <a 
                    href={business.affiliate_signup_url || business.affiliate_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <Button className="w-full bg-[#F59E0B] hover:bg-[#F59E0B]/80 text-[#0F172A] font-semibold">
                      Join Affiliate Program
                    </Button>
                  </a>
                ) : (
                  <Link to={`/affiliate-request?business_id=${business.id}&business_name=${encodeURIComponent(business.name)}`}>
                    <Button className="w-full bg-[#F59E0B] hover:bg-[#F59E0B]/80 text-[#0F172A] font-semibold">
                      Join Affiliate Program
                    </Button>
                  </Link>
                )}
              </div>
            </motion.div>
          </div>
        </div>

        {/* SHARE THIS LISTING — promote, save contact, QR code */}
        <div className="mt-10">
          <ShareThisListingSection business={business} />
        </div>

        {/* Best of Best LiveCast™ — Phase 1 (Premium feature) */}
        <LivecastSection listingSlug={business?.slug} business={business} />

        {/* Live Events & Replays (any listing type) */}
        <LiveEventsSection listing={business} />

        {/* Podcast Link (lightweight, any listing type) */}
        <PodcastCard listing={business} />

        {/* OnlyBest Campaign sections (picks, recommendations, causes, offers, affiliate links) */}
        <OnlyBestSections business={business} />

        {/* Reverse-endorsement social proof: which OnlyBest participants picked this business */}
        <EndorsedBy slug={business.slug} />

        {/* GET YOUR OWN LISTING — converts organic SEO visitors who are local business owners */}
        <div className="mt-6">
          <GetYourListingStrip city={business.city} state={business.state} referrerSlug={business.slug} />
        </div>

        {/* OnlyBest Creator CTA — for visitors who love this listing and want to become a creator */}
        <div className="mt-4">
          <Link to={`/submit?kind=creator&ref=${encodeURIComponent(business.slug)}`} data-testid="onlybest-listing-cta" className="block group">
            <div className="rounded-2xl border border-[#FBBF24]/30 bg-gradient-to-r from-[#FBBF24]/10 via-[#0F172A] to-[#EC4899]/10 px-5 py-4 hover:border-[#FBBF24]/50 transition flex items-center gap-3 flex-wrap">
              <span className="inline-flex w-10 h-10 rounded-xl bg-gradient-to-br from-[#FBBF24] to-[#EC4899] items-center justify-center flex-shrink-0">
                <Crown className="w-5 h-5 text-white" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold text-sm sm:text-base leading-tight">
                  Promote this business as an OnlyBest Creator
                </p>
                <p className="text-[#94A3B8] text-xs sm:text-sm mt-0.5">
                  Love this business? Get your own creator page and share local businesses, rewards, and causes you believe in.
                </p>
              </div>
              <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-gradient-to-r from-[#FBBF24] to-[#EC4899] text-white text-xs sm:text-sm font-semibold group-hover:scale-105 transition flex-shrink-0">
                Become a Creator
              </span>
            </div>
          </Link>
        </div>

        {/* SOCIAL LINKS — moved to bottom (below Promote This Business) so they don't pull visitors off the funnel */}
        {business.social_links && Object.values(business.social_links).some(Boolean) && (
          <div className="mt-10 flex flex-col items-center gap-3" data-testid="bottom-social-links">
            <p className="text-xs text-[#64748B] uppercase tracking-wider">Connect on social</p>
            <div className="flex items-center gap-3 flex-wrap justify-center">
              {Object.entries(business.social_links).map(([platform, url]) => {
                if (!url) return null;
                const Icon = socialIcons[platform];
                if (!Icon) return null;
                return (
                  <a
                    key={platform}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-[#94A3B8] hover:text-white"
                    aria-label={`Visit ${business.name} on ${platform}`}
                    data-testid={`bottom-social-${platform}`}
                  >
                    {typeof Icon === "function" && !Icon.$$typeof ? <Icon /> : <Icon className="w-5 h-5" />}
                  </a>
                );
              })}
            </div>
          </div>
        )}
      </div>
      
      {/* STICKY MOBILE CTA BAR — visible on small screens only */}
      <div
        className="fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-[#0A0F1C]/95 backdrop-blur-xl border-t border-white/10 px-3 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]"
        data-testid="mobile-sticky-cta-bar"
      >
        <div className="grid grid-cols-3 gap-2">
          {business.contact_phone ? (
            <a href={`tel:${business.contact_phone.replace(/\D/g, '')}`} onClick={() => trackCallClick(business)}>
              <Button className="w-full bg-[#10B981] hover:bg-[#059669] text-white text-xs h-10" data-testid="mobile-cta-call-btn">
                <Phone className="w-4 h-4 mr-1" /> Call
              </Button>
            </a>
          ) : (
            <Button disabled className="w-full bg-white/5 text-[#64748B] text-xs h-10">
              <Phone className="w-4 h-4 mr-1" /> Call
            </Button>
          )}
          <Button
            onClick={() => scrollToLeadForm("Mobile Sticky — Claim Offer")}
            className="w-full bg-gradient-to-r from-[#F59E0B] to-[#EC4899] text-white text-xs h-10"
            data-testid="mobile-cta-claim-btn"
          >
            <Gift className="w-4 h-4 mr-1" /> Claim Offer
          </Button>
          <Button
            onClick={scrollToAIChat}
            className="w-full bg-gradient-to-r from-[#7C3AED] to-[#00E5FF] text-white text-xs h-10"
            data-testid="mobile-cta-ai-btn"
          >
            <Bot className="w-4 h-4 mr-1" /> Ask AI
          </Button>
        </div>
      </div>

      {/* Image Gallery Modal */}
      <AnimatePresence>
        {galleryOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
            onClick={() => setGalleryOpen(false)}
          >
            <button
              onClick={() => setGalleryOpen(false)}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
            >
              <X className="w-6 h-6" />
            </button>
            
            <button
              onClick={(e) => { e.stopPropagation(); setGalleryIndex((prev) => (prev > 0 ? prev - 1 : allImages.length - 1)); }}
              className="absolute left-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            
            <motion.img
              key={galleryIndex}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              src={allImages[galleryIndex]}
              alt={`Gallery image ${galleryIndex + 1}`}
              className="max-w-[90vw] max-h-[90vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            
            <button
              onClick={(e) => { e.stopPropagation(); setGalleryIndex((prev) => (prev < allImages.length - 1 ? prev + 1 : 0)); }}
              className="absolute right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
            
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm">
              {galleryIndex + 1} / {allImages.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Latest Updates — full content modal */}
      <ContentDetailModal
        open={!!activeContent}
        onClose={() => setActiveContent(null)}
        item={activeContent}
        businessName={business?.name}
        businessOrgId={business?.organization_id}
      />
    </div>
  );
}

/* ---------------- BEST OF BEST PRESS RELEASE ---------------- */
function renderMarkdownLine(line, key) {
  const t = line.trim();
  if (!t) return null;
  if (t.startsWith("# ")) return <h1 key={key} className="text-3xl sm:text-4xl lg:text-5xl font-bold font-['Outfit'] text-white leading-tight mt-8 mb-4">{t.slice(2)}</h1>;
  if (t.startsWith("## ")) return <h2 key={key} className="text-xl sm:text-2xl font-bold text-[#F4B41A] font-['Outfit'] leading-snug mt-6 mb-3">{t.slice(3)}</h2>;
  if (t.startsWith("### ")) return <h3 key={key} className="text-lg sm:text-xl font-semibold text-white mt-5 mb-2">{t.slice(4)}</h3>;
  const parts = t.split(/(\*\*[^*]+\*\*)/g).map((p, j) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={j} className="text-white font-semibold">{p.slice(2, -2)}</strong>
      : <span key={j}>{p}</span>
  );
  return <p key={key} className="text-[#CBD5E1] leading-relaxed mb-4">{parts}</p>;
}

function BestOfBestPressRelease({ business }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Build a clean 2–3 line teaser from the press release body — strip markdown markers, keep ~280 chars from first prose paragraph.
  const teaser = (() => {
    if (!business.press_release_body) return "";
    const lines = business.press_release_body.split("\n").map(s => s.trim()).filter(Boolean);
    // Skip headings, find first plain paragraph
    const firstPara = lines.find(l => !l.startsWith("#"));
    if (!firstPara) return "";
    // Strip **bold** markers but keep text
    const clean = firstPara.replace(/\*\*/g, "");
    return clean.length > 280 ? clean.slice(0, 280).trimEnd() + "…" : clean;
  })();

  // Headline from first # line, for the modal header
  const headline = (() => {
    if (!business.press_release_body) return "";
    const h = business.press_release_body.split("\n").map(s => s.trim()).find(l => l.startsWith("# "));
    return h ? h.slice(2) : "";
  })();

  // Share helpers — share the public listing URL with the press release headline as the title.
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareTitle = headline || `${business.name} — Best of Best Press Release`;
  const handleCopy = async () => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const ta = document.createElement("textarea");
        ta.value = shareUrl;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { /* noop */ }
  };
  const xShareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`;
  const linkedinShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;

  return (
    <>
      <section
        className="relative w-full overflow-hidden border-b border-white/5 bg-gradient-to-b from-[#070B16] via-[#0B0F1E] to-[#070B16]"
        data-testid="best-of-best-press-release"
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at top, rgba(244,180,26,0.12) 0%, rgba(0,0,0,0) 55%)" }}
        />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 md:pt-14 pb-12 md:pb-16">
          <div className="text-center mb-6 md:mb-8">
            <p className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-[#F4B41A]/15 to-[#F59E0B]/15 border border-[#F4B41A]/30 text-[#F4B41A] text-[11px] sm:text-xs font-bold uppercase tracking-[0.25em]">
              <span className="text-[#F4B41A]">★</span> Best of Best Press Release
            </p>
          </div>

          {business.press_release_image && (
            <div className="relative rounded-3xl overflow-hidden border border-[#F4B41A]/30 shadow-[0_30px_80px_-20px_rgba(244,180,26,0.35)] mb-6 md:mb-8">
              <img
                src={business.press_release_image}
                alt={`${business.name} — Best of Best Press Release`}
                className="block w-full h-auto"
                data-testid="press-release-image"
              />
            </div>
          )}

          <p
            className="max-w-3xl mx-auto text-center text-sm sm:text-base md:text-lg font-semibold text-white mb-5 md:mb-6"
            data-testid="press-release-byline"
          >
            Written by Roe Hubbard, Founder of LocalBest.ai — For Immediate Release
          </p>

          {business.press_release_body && (
            <div className="max-w-3xl mx-auto text-center" data-testid="press-release-cta">
              <button
                type="button"
                onClick={() => setOpen(true)}
                className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-gradient-to-r from-[#F4B41A] to-[#E0A015] hover:from-[#E0A015] hover:to-[#F4B41A] text-[#0A0F1C] font-bold text-sm sm:text-base shadow-lg shadow-[#F4B41A]/30 transition"
                data-testid="press-release-read-full-btn"
              >
                Read Full Article
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </section>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8 bg-black/80 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="press-release-modal"
        >
          <div
            className="relative w-full max-w-3xl max-h-[92vh] flex flex-col rounded-3xl bg-gradient-to-b from-[#0F172A] via-[#0B0F1E] to-[#070B16] border border-[#F4B41A]/40 shadow-[0_30px_80px_-10px_rgba(244,180,26,0.45)] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Sticky top bar (always visible, even when scrolling the article) */}
            <div className="flex-shrink-0 flex items-center justify-between gap-3 px-5 sm:px-8 py-4 border-b border-[#F4B41A]/20 bg-[#0F172A]/95 backdrop-blur">
              <p className="inline-flex items-center gap-2 text-[10px] sm:text-xs font-bold uppercase tracking-[0.25em] text-[#F4B41A]">
                <span>★</span> Best of Best Press Release
              </p>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 text-white"
                aria-label="Close"
                data-testid="press-release-modal-close"
              >
                ✕
              </button>
            </div>

            {/* Scrollable body — image first, then article */}
            <div className="flex-1 overflow-y-auto px-5 sm:px-8 pt-8 pb-8" data-testid="press-release-modal-body">
              {business.press_release_image && (
                <div className="rounded-2xl overflow-hidden border border-[#F4B41A]/30 mb-6">
                  <img
                    src={business.press_release_image}
                    alt={headline || business.name}
                    className="block w-full h-auto"
                  />
                </div>
              )}

              <article>
                {business.press_release_body && business.press_release_body.split(/\n/).map((line, i) => renderMarkdownLine(line, i))}

                <div className="mt-8 pt-6 border-t border-white/10" data-testid="press-release-share-row">
                  <p className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.22em] text-[#F4B41A] mb-3 flex items-center gap-2">
                    <Share2 className="w-3.5 h-3.5" />
                    Share This Press Release
                  </p>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    <button
                      type="button"
                      onClick={handleCopy}
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-xs sm:text-sm font-semibold transition ${copied ? "bg-emerald-500/15 border-emerald-400/50 text-emerald-300" : "bg-white/5 border-white/15 text-white hover:bg-white/10"}`}
                      data-testid="press-release-share-copy"
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
                      {copied ? "Link Copied" : "Copy Link"}
                    </button>
                    <a
                      href={xShareUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/15 hover:bg-white/10 text-white text-xs sm:text-sm font-semibold transition"
                      data-testid="press-release-share-x"
                    >
                      <X className="w-4 h-4" />
                      Share on X
                    </a>
                    <a
                      href={linkedinShareUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/15 hover:bg-white/10 text-white text-xs sm:text-sm font-semibold transition"
                      data-testid="press-release-share-linkedin"
                    >
                      <Linkedin className="w-4 h-4" />
                      Share on LinkedIn
                    </a>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-white/5 text-center">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm font-medium"
                  >
                    Close Article
                  </button>
                </div>
              </article>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
